import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Box, CheckCircle2, AlertTriangle, Trash2, Edit3, UserCheck,
  ShieldAlert, FileText, Save, X, Sparkles, Plus, Download, Printer, Eye,
  Image as ImageIcon, ZoomIn, ExternalLink, ShieldCheck, QrCode, Barcode, ScanBarcode,
  Upload, UploadCloud, FileUp, File as FileIcon, Paperclip, Check, FileSpreadsheet,
  Cpu, HardDrive, Network, Tag, Clock, Calendar, Laptop, Server, Smartphone,
  RotateCcw, CheckSquare, Wrench, RefreshCw, Copy, Hash,
  DollarSign, Building, MapPin, User, Info, ArrowUpRight, Shield, Layers, HelpCircle, Truck,
  Mail, Phone, Briefcase, Search, ChevronDown, CheckCircle, Ticket, Code2, Globe,
  GitBranch, Terminal, Activity, Cloud
} from 'lucide-react';
import { useAssetStore, type Asset, type AssetInvoice, type AssetAttachment, type AuditLog, ownershipLabel } from '../../../store/assetStore';
import { useAssetSettingsStore } from '../../../store/assetSettingsStore';
import { useEmployeeAssetsStore, type AssetEmployee } from '../../../store/employeeAssetsStore';
import { EmployeeCard } from '../../../components/assets/EmployeeAssets';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Avatar, AvatarFallback } from '../../../components/Avatar';
import { useTicketStore } from '../../../store/ticketStore';
import { useAuthStore } from '../../../store/authStore';
import { cn } from '../../../lib/utils';
import { processSelectedFiles, formatFileSize, UploadedFileItem } from '../../../utils/fileUpload';
import toast from 'react-hot-toast';

interface ITSMAssetDetailViewProps {
  assetIdProp?: string;
  onBackToList?: () => void;
  initialEditMode?: boolean;
  autoOpenTab?: 'invoice' | 'gallery' | 'warranty' | 'custody' | 'procurement' | 'documents';
}

type DetailTab = 'specs' | 'custody' | 'procurement' | 'tickets' | 'documents' | 'photos' | 'history';

/** Shown wherever a value was never recorded. */
const NONE = '—';

/** Fields without their own column live in assets.specifications. */
const SPEC_FIELDS = [
  'cpuCoreCount', 'salvageValue', 'acquisitionType',
  // Software & Cloud Operational Details
  'domain', 'hosting', 'devServer', 'prServer', 'prodServer',
  // Software Architecture, Runtime & Governance Specs
  'service', 'environment', 'version', 'component', 'appType',
  'repoUrl', 'buildTag', 'runtime', 'hostingPlatform', 'clusterRegion',
  'containerImage', 'memoryLimit', 'cpuAllocation',
  'servicePort', 'apiBaseUrl', 'healthCheckUrl', 'protocol', 'authScheme',
  'licenseType', 'slaTarget', 'dataClassification',
];

/** Extract or derive operational endpoints and deployment details for digital/cloud assets */
const getDigitalDetails = (asset: Asset | null) => {
  if (!asset) {
    return {
      domain: '',
      hosting: '',
      version: '',
      devServer: '',
      prServer: '',
      prodServer: '',
    };
  }
  const specs = asset.specifications || {};
  return {
    domain: specs.domain || specs.apiBaseUrl || '',
    hosting: specs.hosting || specs.hostingPlatform || '',
    version: specs.version || '',
    devServer: specs.devServer || '',
    prServer: specs.prServer || '',
    prodServer: specs.prodServer || '',
  };
};

const EMPTY_MODAL = { assigneeName: '', employeeId: '', department: '', site: '', reason: '', condition: 'Good', notes: '', serviceProvider: '' };

// Barcode / QR tag generation is hidden across the asset view. Flip to true to restore it.
const SHOW_BARCODE = false;

const initials = (name?: string) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

/**
 * Asset detail. The asset is loaded from GET /assets/assets/:idOrCode (by
 * code from the URL) — never guessed from a cached list. Every action calls
 * the assets service: edit (PATCH), check out/in (assign/return), service
 * (maintenance), retire, delete, photos and documents (attachments), and the
 * History tab reads assets.asset_history.
 */
export function ITSMAssetDetailView({ assetIdProp, onBackToList, initialEditMode = false, autoOpenTab }: ITSMAssetDetailViewProps) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const {
    fetchAsset, updateAsset, deleteAsset, assignAsset, returnAsset, retireAsset,
    addAttachment, deleteAttachment, createMaintenance, updateMaintenance, fetchHistory,
  } = useAssetStore();
  const { settings, fetchSettings } = useAssetSettingsStore();
  const { fetchDirectory, offices, fetchOffices } = useEmployeeAssetsStore();
  const { tickets, fetchTickets, createTicket } = useTicketStore();
  const { user } = useAuthStore();

  const rawParam = assetIdProp || params.registryNumber || params.id || '';
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [history, setHistory] = useState<AuditLog[]>([]);
  const [employees, setEmployees] = useState<(AssetEmployee & { department: string })[]>([]);
  const [busy, setBusy] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlTab = searchParams.get('tab') as DetailTab | null;
  const isEditParam = searchParams.get('edit') === 'true';

  const [activeTab, setActiveTabState] = useState<DetailTab>(
    urlTab && ['specs', 'custody', 'procurement', 'tickets', 'photos', 'history'].includes(urlTab) ? urlTab : 'specs'
  );

  const setActiveTab = (tab: DetailTab) => {
    setActiveTabState(tab);
    const newParams = new URLSearchParams(location.search);
    if (tab === 'specs') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tab);
    }
    const newSearch = newParams.toString() ? `?${newParams.toString()}` : '';
    navigate(`${location.pathname}${newSearch}`, { replace: true });
  };

  const [ticketFilter, setTicketFilter] = useState<'all' | 'open'>('all');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [barcodeDataUrl, setBarcodeDataUrl] = useState<string>('');
  const [tagMode, setTagMode] = useState<'barcode' | 'qr'>('barcode');
  const barcodeSvgRef = useRef<SVGSVGElement | null>(null);
  const modalBarcodeSvgRef = useRef<SVGSVGElement | null>(null);

  const isCloudOrSoftware = useMemo(() => {
    if (!asset) return false;
    const t = String(asset.type || '').toLowerCase();
    const c = String(asset.category || '').toLowerCase();
    const sc = String(asset.subcategory || '').toLowerCase();
    const n = String(asset.name || '').toLowerCase();
    const m = String(asset.model || '').toLowerCase();

    const digitalKeywords = [
      'software',
      'cloud',
      'server',
      'microservice',
      'service',
      'api',
      'saas',
      'application',
      'app',
      'platform',
      'infrastructure',
      'database',
      'db',
      'virtual',
      'vm',
      'cluster',
      'instance',
      'endpoint',
      'hosting',
      'domain',
    ];

    return digitalKeywords.some((kw) =>
      t.includes(kw) || c.includes(kw) || sc.includes(kw) || n.includes(kw) || m.includes(kw)
    );
  }, [asset]);

  const isSoftware = isCloudOrSoftware;

  const digitalDetails = useMemo(() => getDigitalDetails(asset), [asset]);

  // Dynamic custody & assignment information from live application state
  const custodyDetails = useMemo(() => {
    if (!asset) return { name: 'In Stock / Unassigned', code: '', dept: '', loc: '', status: 'In Stock' };
    const isSw = String(asset.type || '').toLowerCase() === 'software' || String(asset.category || '').toLowerCase() === 'software';
    const assigneeCode = asset.assignee?.code ? ` [${asset.assignee.code}]` : '';
    const assigneeDept = asset.assignee?.department || asset.department || '';
    const resolvedSite = asset.site || (asset.officeId ? offices.find((o) => o.id === asset.officeId)?.name : '') || '';
    const assigneeLoc = asset.assignee?.location || resolvedSite || asset.location || '';
    const isAssigned = asset.status === 'Assigned';
    const defaultUnassigned = isSw ? 'Platform Team / Engineering' : 'In Stock / Unassigned';
    const name = isAssigned ? (asset.assignedToName || (asset.assignee as any)?.name || 'Assigned') : defaultUnassigned;
    return {
      name,
      code: assigneeCode,
      dept: assigneeDept,
      loc: assigneeLoc,
      status: asset.status || 'Active',
      formattedText: isAssigned
        ? `${name}${assigneeCode}${assigneeDept ? ` · ${assigneeDept}` : ''}${assigneeLoc ? ` · ${assigneeLoc}` : ''}`
        : defaultUnassigned,
    };
  }, [asset, offices]);

  // QR Payload with full dynamic asset details
  const qrPayload = useMemo(() => {
    if (!asset) return '';
    return [
      `Company: ${(asset as any)?.company || 'TeamOne'}`,
      `Asset Code: ${asset.asset_code || asset.code || ''}`,
      `Asset Name: ${asset.name || ''}`,
      `Model: ${asset.model || asset.manufacturer || ''}`,
      `Serial Number: ${asset.serial_number || 'N/A'}`,
      `Category: ${asset.category || 'IT Asset'}`,
      `Status: ${asset.status || ''}`,
      `Custody / Assigned To: ${custodyDetails.formattedText}`,
      `Department: ${custodyDetails.dept || 'N/A'}`,
      `Location: ${custodyDetails.loc || 'N/A'}`,
    ].join('\n');
  }, [asset, custodyDetails]);

  // Generate QR Code
  useEffect(() => {
    if (!qrPayload) {
      setQrCodeDataUrl('');
      return;
    }
    QRCode.toDataURL(qrPayload, {
      width: 280,
      margin: 1,
      color: {
        dark: '#0f172a',
        light: '#ffffff',
      },
      errorCorrectionLevel: 'M',
    })
      .then((url) => setQrCodeDataUrl(url))
      .catch((err) => console.error('Failed to generate QR code', err));
  }, [qrPayload]);

  // Generate Static Barcode (Code 128) encoding asset tag code with dynamic visual metadata
  useEffect(() => {
    const code = (asset?.asset_code || asset?.code || '').trim();
    if (!code) {
      setBarcodeDataUrl('');
      return;
    }

    // Render left panel SVG barcode
    if (barcodeSvgRef.current) {
      try {
        JsBarcode(barcodeSvgRef.current, code, {
          format: 'CODE128',
          lineColor: '#0f172a',
          width: 1.8,
          height: 48,
          displayValue: true,
          text: code,
          font: 'monospace',
          textAlign: 'center',
          textPosition: 'bottom',
          textMargin: 3,
          fontSize: 13,
          background: '#ffffff',
          margin: 6,
        });
      } catch (err) {
        console.error('Failed to render barcode SVG', err);
      }
    }

    // Render modal SVG barcode
    if (modalBarcodeSvgRef.current) {
      try {
        JsBarcode(modalBarcodeSvgRef.current, code, {
          format: 'CODE128',
          lineColor: '#0f172a',
          width: 2.2,
          height: 56,
          displayValue: true,
          text: code,
          font: 'monospace',
          textAlign: 'center',
          textPosition: 'bottom',
          textMargin: 4,
          fontSize: 14,
          background: '#ffffff',
          margin: 8,
        });
      } catch (err) {
        console.error('Failed to render modal barcode SVG', err);
      }
    }

    // Generate PNG Data URL for download
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, code, {
        format: 'CODE128',
        lineColor: '#0f172a',
        width: 2.2,
        height: 60,
        displayValue: true,
        text: code,
        font: 'monospace',
        textAlign: 'center',
        textPosition: 'bottom',
        textMargin: 5,
        fontSize: 14,
        background: '#ffffff',
        margin: 10,
      });
      setBarcodeDataUrl(canvas.toDataURL('image/png'));
    } catch (err) {
      console.error('Failed to generate barcode data URL', err);
    }
  }, [asset?.asset_code, asset?.code, tagMode]);

  const load = useCallback(async () => {
    if (!rawParam) { setLoadState('missing'); return; }
    try {
      const a = await fetchAsset(rawParam);
      setAsset(a);
      setLoadState('ready');
      fetchHistory({ assetId: a.id, limit: 200 }).then(setHistory).catch(() => setHistory([]));
    } catch (e: any) {
      setAsset(null);
      if (/not found/i.test(e.message)) setLoadState('missing');
      else { setLoadError(e.message); setLoadState('error'); }
    }
  }, [rawParam, fetchAsset, fetchHistory]);

  useEffect(() => { setLoadState('loading'); load(); }, [load]);
  useEffect(() => { fetchTickets(); fetchSettings(); }, [fetchTickets, fetchSettings]);
  useEffect(() => {
    // Employees from people.employees via the assets service (no People permission needed).
    fetchDirectory()
      .then((rows) => setEmployees(rows.map((e) => ({ ...e, department: e.department || '' }))))
      .catch(() => undefined);
    fetchOffices().catch(() => undefined);
  }, [fetchDirectory, fetchOffices]);

  /** Apply a server-returned asset and refresh its history. */
  const applyAsset = (a: Asset) => {
    setAsset(a);
    fetchHistory({ assetId: a.id, limit: 200 }).then(setHistory).catch(() => undefined);
  };

  /** Run a write; report the server's error instead of pretending it worked. */
  const run = async (fn: () => Promise<Asset | void>, ok: string) => {
    setBusy(true);
    try {
      const result = await fn();
      if (result) applyAsset(result);
      toast.success(ok);
      return true;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Linked support tickets (support service), matched by asset id or code.
  const linkedTickets = useMemo(() => {
    if (!asset) return [];
    return tickets.filter(
      (t) =>
        (t.assetId && String(t.assetId) === String(asset.id)) ||
        (asset.asset_code && t.assetTag && t.assetTag.toLowerCase() === asset.asset_code.toLowerCase()) ||
        ((t as any).primaryAssetId && String((t as any).primaryAssetId) === String(asset.id)) ||
        ((t as any).primaryAssetCode && asset.asset_code && String((t as any).primaryAssetCode).toLowerCase() === asset.asset_code.toLowerCase())
    );
  }, [tickets, asset]);

  const openTicketsCount = useMemo(
    () => linkedTickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed').length,
    [linkedTickets]
  );

  const displayedTickets = useMemo(
    () => (ticketFilter === 'open' ? linkedTickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed') : linkedTickets),
    [linkedTickets, ticketFilter]
  );

  // In-place edit state
  const [isEditing, setIsEditing] = useState<boolean>(initialEditMode || isEditParam);
  const [editForm, setEditForm] = useState<Record<string, any>>({});

  // Action Modals State
  const [activeModal, setActiveModal] = useState<'checkout' | 'checkin' | 'maintenance' | 'retire' | 'barcode' | 'ticket' | null>(null);
  const [modalInput, setModalInput] = useState(EMPTY_MODAL);
  const [employeeSearch, setEmployeeSearch] = useState('');

  // In-app Support Ticket Form State
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    type: 'Fault',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    urgency: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    impact: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    category: '',
    requesterName: '',
    requesterEmail: '',
  });
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [lastCreatedTicket, setLastCreatedTicket] = useState<{
    id: string | number;
    ticketNumber: string;
    subject: string;
  } | null>(null);

  const filteredEmployees = useMemo(() => {
    if (!employeeSearch.trim()) return employees.slice(0, 50);
    const q = employeeSearch.toLowerCase();
    return employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.code && e.code.toLowerCase().includes(q)) ||
        (e.department && e.department.toLowerCase().includes(q)) ||
        (e.designation && e.designation.toLowerCase().includes(q)) ||
        (e.email && e.email.toLowerCase().includes(q))
    );
  }, [employees, employeeSearch]);

  const selectedEmployee = useMemo(
    () => employees.find((x) => x.id === modalInput.employeeId),
    [employees, modalInput.employeeId]
  );

  // Document Viewer Modal State
  const [documentModal, setDocumentModal] = useState<{
    isOpen: boolean;
    title: string;
    docType: 'invoice' | 'warranty' | 'handover';
    docNumber?: string;
    docAmount?: number;
    docDate?: string;
    vendor?: string;
    s3Key?: string;
    fileName?: string;
    fileUrl?: string;
    fileSize?: string;
    isPdf?: boolean;
    isImage?: boolean;
  }>({ isOpen: false, title: '', docType: 'invoice' });

  // Photo Gallery & Lightbox Modals
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [imageUploadMode, setImageUploadMode] = useState<'file' | 'url'>('file');
  const [stagedPhotos, setStagedPhotos] = useState<UploadedFileItem[]>([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [isDraggingPhotos, setIsDraggingPhotos] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ isOpen: boolean; url: string; index: number }>({ isOpen: false, url: '', index: 0 });

  // Add Document Modal State
  const [showAddDocModal, setShowAddDocModal] = useState<{
    isOpen: boolean;
    type: 'invoice' | 'warranty' | 'handover_signoff' | 'contract' | 'other';
  }>({ isOpen: false, type: 'invoice' });
  const [stagedDoc, setStagedDoc] = useState<UploadedFileItem | null>(null);
  const [isDraggingDoc, setIsDraggingDoc] = useState(false);
  const [newDocMeta, setNewDocMeta] = useState({ title: '', invoiceNumber: '', vendorName: '', amount: 0, docDate: '', validUntil: '' });

  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const docFileInputRef = useRef<HTMLInputElement>(null);

  // Auto open document or gallery based on props or URL path (after the asset loads).
  useEffect(() => {
    if (!asset) return;
    if (isCloudOrSoftware) {
      if (['custody', 'procurement', 'documents', 'photos'].includes(activeTab)) {
        setActiveTab('specs');
      }
      return;
    }
    const path = location.pathname.toLowerCase();
    if (autoOpenTab === 'invoice' || path.includes('/invoice') || path.includes('/invocie')) {
      setActiveTab('procurement');
      if (asset.invoices.length) handleOpenInvoiceDoc(asset.invoices[0]);
    } else if (autoOpenTab === 'warranty' || path.includes('/warranty')) {
      setActiveTab('procurement');
      if (asset.attachments.length) handleOpenWarrantyDoc(asset.attachments[0]);
    } else if (autoOpenTab === 'documents' || path.includes('/documents')) {
      setActiveTab('procurement');
    } else if (autoOpenTab === 'gallery') {
      setActiveTab('photos');
    } else if (autoOpenTab === 'custody' || path.includes('/custody')) {
      setActiveTab('custody');
    } else if (autoOpenTab === 'procurement' || path.includes('/procurement')) {
      setActiveTab('procurement');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenTab, location.pathname, asset?.id, isCloudOrSoftware]);

  // Ensure digital/cloud assets do not land on physical tabs
  useEffect(() => {
    if (isCloudOrSoftware && ['custody', 'procurement', 'photos', 'documents'].includes(activeTab)) {
      setActiveTab('specs');
    }
  }, [isCloudOrSoftware, activeTab]);

  // Clipboard paste → stage screenshots as photos (hardware assets only).
  useEffect(() => {
    if (!asset || isCloudOrSoftware) return;
    const onGlobalPaste = (e: ClipboardEvent) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') return;
      handlePhotoPaste(e);
    };
    window.addEventListener('paste', onGlobalPaste);
    return () => window.removeEventListener('paste', onGlobalPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.asset_code, isCloudOrSoftware]);

  if (loadState === 'loading') {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading asset…</div>;
  }

  if (!asset) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        <p>{loadState === 'error' ? loadError : `No asset found for "${rawParam}".`}</p>
        <button
          onClick={() => (onBackToList ? onBackToList() : navigate('/assets/registry'))}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
        >
          <ArrowLeft className="size-4" /> Back to asset registry
        </button>
      </div>
    );
  }

  // ── depreciation from Asset settings (method, salvage %, useful life) ──
  const depreciationPolicy = settings?.depreciation;
  const usefulLifeYears = (() => {
    const cat = `${asset.category || ''} ${asset.type || ''}`.toLowerCase();
    if (!depreciationPolicy) return null;
    if (/server|storage|network/.test(cat)) return depreciationPolicy.server_useful_life_years;
    if (/mobile|phone|tablet/.test(cat)) return depreciationPolicy.mobile_useful_life_years;
    if (/monitor|peripheral|audio|printer/.test(cat)) return depreciationPolicy.peripheral_useful_life_years;
    return depreciationPolicy.laptop_useful_life_years;
  })();
  const salvageValue =
    asset.specifications.salvageValue !== undefined && asset.specifications.salvageValue !== ''
      ? Number(asset.specifications.salvageValue)
      : asset.purchaseCost !== null && asset.purchaseCost !== undefined && depreciationPolicy
        ? Math.round(asset.purchaseCost * (depreciationPolicy.default_salvage_percentage / 100))
        : null;

  const currency = settings?.procurement.default_currency || 'INR';
  const money = (n?: number | null) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return NONE;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(n)); }
    catch { return `${currency} ${Number(n).toLocaleString()}`; }
  };
  const monthlyDepreciation =
    asset.purchaseCost !== null && asset.purchaseCost !== undefined && usefulLifeYears && depreciationPolicy?.method !== 'No Depreciation'
      ? Math.max(0, (asset.purchaseCost - (salvageValue ?? 0)) / (usefulLifeYears * 12))
      : null;

  // Edit Handlers
  const handleStartEdit = () => {
    setEditForm({
      name: asset.name,
      asset_code: asset.asset_code || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      serial_number: asset.serial_number || '',
      ownership: asset.ownership || 'owned',
      location: asset.location || '',
      site: asset.site || '',
      department: asset.department || '',
      os: asset.os || '',
      ram: asset.ram || '',
      storage: asset.storage || '',
      cpuSpeed: asset.cpuSpeed || '',
      macAddress: asset.macAddress || '',
      ipAddress: asset.ipAddress || '',
      notes: asset.notes || '',
      remarks: asset.remarks || '',
      purchaseCost: asset.purchaseCost !== null && asset.purchaseCost !== undefined ? String(asset.purchaseCost) : '',
      cpuCoreCount: asset.specifications?.cpuCoreCount ?? '',
      domain: asset.specifications?.domain !== undefined ? asset.specifications.domain : (digitalDetails.domain || ''),
      hosting: asset.specifications?.hosting || asset.specifications?.hostingPlatform || (digitalDetails.hosting || ''),
      devServer: asset.specifications?.devServer !== undefined ? asset.specifications.devServer : (digitalDetails.devServer || ''),
      prServer: asset.specifications?.prServer !== undefined ? asset.specifications.prServer : (digitalDetails.prServer || ''),
      prodServer: asset.specifications?.prodServer !== undefined ? asset.specifications.prodServer : (digitalDetails.prodServer || ''),
      service: asset.specifications?.service || '',
      environment: asset.specifications?.environment || 'Production',
      version: asset.specifications?.version || (digitalDetails.version || ''),
      component: asset.specifications?.component || '',
      appType: asset.specifications?.appType || '',
      repoUrl: asset.specifications?.repoUrl || '',
      buildTag: asset.specifications?.buildTag || '',
      runtime: asset.specifications?.runtime || '',
      hostingPlatform: asset.specifications?.hostingPlatform || asset.specifications?.hosting || '',
      clusterRegion: asset.specifications?.clusterRegion || '',
      containerImage: asset.specifications?.containerImage || '',
      memoryLimit: asset.specifications?.memoryLimit || '',
      cpuAllocation: asset.specifications?.cpuAllocation || '',
      servicePort: asset.specifications?.servicePort || '',
      apiBaseUrl: asset.specifications?.apiBaseUrl || '',
      healthCheckUrl: asset.specifications?.healthCheckUrl || '',
      protocol: asset.specifications?.protocol || '',
      authScheme: asset.specifications?.authScheme || '',
      licenseType: asset.specifications?.licenseType || '',
      slaTarget: asset.specifications?.slaTarget || '',
      dataClassification: asset.specifications?.dataClassification || '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditForm({});
    setIsEditing(false);
    if (searchParams.get('edit')) {
      const newParams = new URLSearchParams(location.search);
      newParams.delete('edit');
      const newSearch = newParams.toString() ? `?${newParams.toString()}` : '';
      navigate(`${location.pathname}${newSearch}`, { replace: true });
    }
  };

  const handleSaveEdit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!String(editForm.name || '').trim()) {
      toast.error('Asset name is required');
      return;
    }
    if (editForm.asset_code !== undefined && !String(editForm.asset_code || '').trim()) {
      toast.error('Asset ID cannot be empty');
      return;
    }
    const specifications = { ...(asset.specifications || {}) };
    const payload: Record<string, any> = {};
    for (const [k, v] of Object.entries(editForm)) {
      if (SPEC_FIELDS.includes(k)) {
        if (v === '' || v === null) delete specifications[k];
        else specifications[k] = v;
      } else {
        payload[k] = v === '' ? null : v;
      }
    }
    if (editForm.hosting) {
      specifications.hostingPlatform = editForm.hosting;
    }
    payload.name = String(editForm.name).trim();
    if (payload.asset_code !== undefined && payload.asset_code !== null) {
      payload.asset_code = String(payload.asset_code).trim();
    }
    if (payload.purchaseCost !== undefined && payload.purchaseCost !== null) {
      const num = Number(payload.purchaseCost);
      payload.purchaseCost = isNaN(num) ? null : num;
    }
    payload.specifications = specifications;
    if (await run(() => updateAsset(asset.id, payload), `${asset.asset_code} updated`)) {
      setIsEditing(false);
      if (searchParams.get('edit')) {
        const newParams = new URLSearchParams(location.search);
        newParams.delete('edit');
        const newSearch = newParams.toString() ? `?${newParams.toString()}` : '';
        navigate(`${location.pathname}${newSearch}`, { replace: true });
      }
    }
  };

  // Clipboard Paste Support (Ctrl+V anywhere)
  async function handlePhotoPaste(e: React.ClipboardEvent | ClipboardEvent) {
    if (!asset) return;
    const clipboardData = (e as React.ClipboardEvent).clipboardData || (e as ClipboardEvent).clipboardData;
    if (!clipboardData) return;
    const filesToProcess: File[] = [];
    const items = clipboardData.items;
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            let fileName = file.name;
            if (!fileName || fileName === 'image.png' || fileName === 'blob' || fileName === 'file') {
              const timeStr = new Date().toISOString().replace(/[:.]/g, '-');
              const ext = file.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
              fileName = `snap-${asset.asset_code}-${timeStr}.${ext}`;
            }
            filesToProcess.push(new File([file], fileName, { type: file.type }));
          }
        }
      }
    }
    if (filesToProcess.length > 0) {
      if (e.cancelable) e.preventDefault();
      try {
        const processed = await processSelectedFiles(filesToProcess, 'assets', `photos/${asset.asset_code}`, {
          assetId: asset.id,
          assetTag: asset.asset_code,
          kind: 'photo',
        });
        const onlyImages = processed.filter((p) => p.isImage);
        if (onlyImages.length > 0) {
          setStagedPhotos((prev) => [...prev, ...onlyImages]);
          setImageUploadMode('file');
          setShowAddImageModal(true);
          setActiveTab('photos');
        }
      } catch {
        toast.error('Could not read the pasted image');
      }
    }
  }

  // Photo handlers
  const handlePhotoFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      const processed = await processSelectedFiles(files, 'assets', `photos/${asset.asset_code}`, {
        assetId: asset.id,
        assetTag: asset.asset_code,
        kind: 'photo',
      });
      const onlyImages = processed.filter((item) => item.isImage);
      if (onlyImages.length === 0) {
        toast.error('Please select image files (PNG, JPG, WEBP, GIF, SVG)');
        return;
      }
      setStagedPhotos((prev) => [...prev, ...onlyImages]);
    } catch {
      toast.error('Could not read the image files');
    }
  };

  const handleAddPhotoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (imageUploadMode === 'file') {
      if (stagedPhotos.length === 0) { toast.error('Select at least one photo'); return; }
      const failed = stagedPhotos.filter((p) => !p.fileUrl || p.fileUrl.startsWith('data:'));
      const ok = stagedPhotos.filter((p) => !failed.includes(p));
      const saved = await run(async () => {
        let latest: Asset | undefined;
        for (const p of ok) {
          latest = await addAttachment(asset.id, { kind: 'image', filename: p.name, path: p.fileUrl, mimeType: p.type, sizeBytes: p.sizeBytes });
        }
        return latest;
      }, `${ok.length} photo(s) added`);
      if (failed.length) toast.error(`${failed.length} photo(s) could not be uploaded to storage`);
      if (saved) { setStagedPhotos([]); setShowAddImageModal(false); }
    } else {
      const url = newImageUrl.trim();
      if (!/^https?:\/\//i.test(url)) { toast.error('Enter an image URL starting with http:// or https://'); return; }
      const saved = await run(
        () => addAttachment(asset.id, { kind: 'image', filename: url.split('/').pop() || 'image', path: url }),
        'Photo added'
      );
      if (saved) { setNewImageUrl(''); setShowAddImageModal(false); }
    }
  };

  const handleDeletePhoto = async (indexToDelete: number) => {
    const file = asset.files.filter((f) => f.kind === 'image')[indexToDelete];
    if (!file) return;
    if (await run(() => deleteAttachment(asset.id, file.id), 'Photo removed')) {
      setLightboxImage({ isOpen: false, url: '', index: 0 });
    }
  };

  // Document Handlers
  const handleDocFileSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      const subFolder = showAddDocModal.type === 'invoice' ? 'invoices' : 'documents';
      const processed = await processSelectedFiles(files, 'assets', subFolder, {
        assetId: asset.id,
        assetTag: asset.asset_code,
        kind: showAddDocModal.type,
      });
      if (processed.length > 0) {
        const item = processed[0];
        setStagedDoc(item);
        if (!newDocMeta.title) setNewDocMeta((prev) => ({ ...prev, title: item.name.replace(/\.[^/.]+$/, '') }));
      }
    } catch {
      toast.error('Could not read the document');
    }
  };

  const handleOpenAddDocModal = (type: 'invoice' | 'warranty' | 'handover_signoff' | 'contract' | 'other') => {
    setStagedDoc(null);
    setNewDocMeta({
      title: '',
      invoiceNumber: type === 'invoice' ? asset.invoiceNumber || '' : '',
      vendorName: asset.vendor || '',
      amount: asset.purchaseCost ?? 0,
      docDate: asset.purchaseDate || '',
      validUntil: type === 'warranty' ? asset.warrantyUntil || '' : '',
    });
    setShowAddDocModal({ isOpen: true, type });
  };

  const handleSaveDocumentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stagedDoc) { toast.error('Select a document file'); return; }
    if (!stagedDoc.fileUrl || stagedDoc.fileUrl.startsWith('data:')) {
      toast.error('The file could not be uploaded to storage, so it was not attached');
      return;
    }
    const type = showAddDocModal.type;
    const kind = type === 'invoice' ? 'invoice' : type === 'warranty' ? 'warranty' : 'document';
    const metadata: Record<string, any> = kind === 'invoice'
      ? { invoice_number: newDocMeta.invoiceNumber || null, vendor: newDocMeta.vendorName || null, amount: Number(newDocMeta.amount) || null, invoice_date: newDocMeta.docDate || null, currency: settings?.procurement.default_currency }
      : { title: newDocMeta.title || stagedDoc.name, document_type: type, valid_until: newDocMeta.validUntil || null };

    const saved = await run(async () => {
      let latest = await addAttachment(asset.id, { kind, filename: stagedDoc.name, path: stagedDoc.fileUrl, mimeType: stagedDoc.type, sizeBytes: stagedDoc.sizeBytes, metadata });
      // Keep the asset's own fields in step when they were blank.
      const patch: Record<string, any> = {};
      if (kind === 'invoice' && newDocMeta.invoiceNumber && !asset.invoiceNumber) patch.invoiceNumber = newDocMeta.invoiceNumber;
      if (kind === 'warranty' && newDocMeta.validUntil && newDocMeta.validUntil !== asset.warrantyUntil) patch.warrantyUntil = newDocMeta.validUntil;
      if (Object.keys(patch).length) latest = await updateAsset(asset.id, patch);
      return latest;
    }, kind === 'invoice' ? 'Invoice attached' : 'Document attached');
    if (saved) {
      setShowAddDocModal({ isOpen: false, type: 'invoice' });
      setStagedDoc(null);
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Remove this invoice from the asset?')) return;
    await run(() => deleteAttachment(asset.id, invoiceId), 'Invoice removed');
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!window.confirm('Remove this document from the asset?')) return;
    await run(() => deleteAttachment(asset.id, attachmentId), 'Document removed');
  };

  const isPdfFile = (url?: string, name?: string) => Boolean(url?.startsWith('data:application/pdf') || /\.pdf$/i.test(name || ''));
  const isImageFile = (url?: string, name?: string) => Boolean(url?.startsWith('data:image/') || /\.(png|jpe?g|webp|gif|svg)$/i.test(name || ''));

  // Document modal viewer — real attachment values only.
  function handleOpenInvoiceDoc(inv?: AssetInvoice) {
    if (!asset) return;
    const target = inv || asset.invoices[0];
    if (!target) { toast.error('No invoice is attached to this asset'); return; }
    setDocumentModal({
      isOpen: true,
      title: `Invoice ${target.invoiceNumber || target.fileName}`,
      docType: 'invoice',
      docNumber: target.invoiceNumber || NONE,
      docAmount: target.amount || undefined,
      docDate: target.invoiceDate || NONE,
      vendor: target.vendorName || asset.vendor || NONE,
      s3Key: target.fileUrl,
      fileName: target.fileName,
      fileUrl: target.fileUrl,
      fileSize: target.fileSize,
      isPdf: isPdfFile(target.fileUrl, target.fileName),
      isImage: isImageFile(target.fileUrl, target.fileName),
    });
  }

  function handleOpenWarrantyDoc(att?: AssetAttachment) {
    if (!asset) return;
    const target = att || asset.attachments[0];
    if (!target) { toast.error('No document is attached to this asset'); return; }
    setDocumentModal({
      isOpen: true,
      title: target.name,
      docType: target.type === 'warranty' ? 'warranty' : 'handover',
      docNumber: NONE,
      docDate: target.uploadedAt ? target.uploadedAt.slice(0, 10) : NONE,
      vendor: asset.vendor || asset.manufacturer || NONE,
      s3Key: target.fileUrl,
      fileName: target.fileName,
      fileUrl: target.fileUrl,
      fileSize: target.fileSize,
      isPdf: isPdfFile(target.fileUrl, target.fileName),
      isImage: isImageFile(target.fileUrl, target.fileName),
    });
  }

  const handleDownloadDocument = (filename: string, fileUrl?: string) => {
    if (!fileUrl) { toast.error('This file is not available'); return; }
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeModal = () => { setActiveModal(null); setModalInput(EMPTY_MODAL); setEmployeeSearch(''); };

  const handleOpenTicketModal = () => {
    if (!asset) return;
    const defaultSubject = `Hardware issue: ${asset.name} (${asset.asset_code})`;
    const defaultRequesterName =
      asset.assignedToName ||
      (asset.assignee as any)?.name ||
      user?.name ||
      '';
    const defaultRequesterEmail =
      (asset.assignee as any)?.email ||
      user?.email ||
      '';
    const defaultCategory = asset.category || asset.type || 'Hardware';

    setTicketForm({
      subject: defaultSubject,
      description: '',
      type: 'Fault',
      priority: 'medium',
      urgency: 'medium',
      impact: 'medium',
      category: defaultCategory,
      requesterName: defaultRequesterName,
      requesterEmail: defaultRequesterEmail,
    });
    setActiveModal('ticket');
  };

  const handleCreateTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    if (!ticketForm.subject.trim()) {
      toast.error('Ticket subject is required');
      return;
    }

    setIsSubmittingTicket(true);
    try {
      const created = await createTicket({
        subject: ticketForm.subject.trim(),
        description: ticketForm.description.trim(),
        type: ticketForm.type,
        priority: ticketForm.priority,
        urgency: ticketForm.urgency,
        impact: ticketForm.impact,
        category: ticketForm.category || asset.category || 'Hardware',
        assetCategory: ticketForm.category || asset.category || 'Hardware',
        primaryAssetId: asset.id,
        assetId: asset.id,
        assetTag: asset.asset_code,
        assetCode: asset.asset_code,
        originModule: 'assets',
        source: 'Asset App',
        client: ticketForm.requesterName.trim() || user?.name || 'Asset User',
        requesterName: ticketForm.requesterName.trim() || user?.name || 'Asset User',
        requesterEmail: ticketForm.requesterEmail.trim() || user?.email || '',
        requesterId: (asset.assignee as any)?.id || user?.id || undefined,
      });

      const ticketNo = created?.ticketNumber || (created ? `TB#${created.id}` : 'TB#New');
      setLastCreatedTicket({
        id: created?.id || ticketNo,
        ticketNumber: ticketNo,
        subject: ticketForm.subject.trim(),
      });

      toast.success(
        `Ticket ${ticketNo} logged in Support and linked to ${asset.asset_code}!`,
        { duration: 5000, icon: '🎫' }
      );

      closeModal();
      setActiveTab('tickets');
      await fetchTickets();
    } catch (err: any) {
      console.error('Failed to create ticket for asset:', err);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  // Workflow Handlers: Check-Out / Check-In / Service / Retire
  const handleCheckOutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalInput.employeeId) { toast.error('Choose the employee'); return; }
    const emp = employees.find((x) => x.id === modalInput.employeeId);
    if (await run(async () => {
      let a = await assignAsset(asset.id, modalInput.employeeId, modalInput.notes || undefined);
      if (modalInput.site && modalInput.site !== a.site) a = await updateAsset(asset.id, { site: modalInput.site });
      return a;
    }, `Checked out to ${emp?.name || 'employee'}`)) closeModal();
  };

  const handleCheckInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(async () => {
      const a = await returnAsset(asset.id, { condition: modalInput.condition, notes: modalInput.notes || undefined });
      if (modalInput.condition === 'Needs Repair') {
        const job = await createMaintenance({ assetId: asset.id, maintenance_type: 'Repair', scheduled_date: new Date().toISOString().slice(0, 10), description: modalInput.notes || 'Returned needing repair' });
        await updateMaintenance(job.id, { status: 'in_progress' });
        return fetchAsset(asset.id);
      }
      return a;
    }, modalInput.condition === 'Needs Repair' ? 'Checked in and sent for repair' : 'Checked in to stock')) closeModal();
  };

  const handleLogMaintenanceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(async () => {
      const job = await createMaintenance({
        assetId: asset.id, maintenance_type: 'Repair', scheduled_date: new Date().toISOString().slice(0, 10),
        vendor: modalInput.serviceProvider || undefined, description: modalInput.notes || undefined,
      });
      await updateMaintenance(job.id, { status: 'in_progress' });
      return fetchAsset(asset.id);
    }, 'Service started — asset is Under Maintenance')) closeModal();
  };

  const handleRetireSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => retireAsset(asset.id, { reason: modalInput.reason || undefined }), `${asset.asset_code} retired`)) closeModal();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete asset ${asset.asset_code}? It will be removed from the register.`)) return;
    setBusy(true);
    try {
      await deleteAsset(asset.id);
      toast.success(`${asset.asset_code} deleted`);
      closeModal();
      if (onBackToList) onBackToList();
      else navigate('/assets/registry');
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete asset');
    } finally {
      setBusy(false);
    }
  };

  // Copy helper
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${label} to clipboard!`);
  };

  // Warranty days remaining (plain value — no hooks after the early returns above).
  const warrantyDays = (() => {
    if (!asset.warrantyUntil) return null;
    const [y, m, d] = asset.warrantyUntil.split('-').map(Number);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((new Date(y, m - 1, d).getTime() - today.getTime()) / 86_400_000);
  })();

  // Primary image
  const primaryImage = asset.images && asset.images.length > 0 ? asset.images[0] : null;

  return (
    <div className="space-y-3.5 bg-background min-h-full text-foreground text-xs font-sans pb-4">
      {/* 1. TOP ACTION BAR */}
      <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-2xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Left: Identity (All in a single line) */}
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <button
              onClick={() => (onBackToList ? onBackToList() : navigate('/assets/registry'))}
              className="inline-flex items-center justify-center size-8 rounded-md border border-border bg-card hover:bg-secondary text-foreground transition-colors shrink-0"
              title="Back to Asset Management"
            >
              <ArrowLeft className="size-4" />
            </button>

            <div className="flex items-center gap-2.5 flex-wrap min-w-0">
              <h1 className="text-foreground flex items-center gap-2 font-display text-page-title shrink-0">
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="h-7 px-2 bg-background border border-input rounded text-sm font-semibold text-foreground outline-none focus:ring-1 focus:ring-ring w-80"
                  />
                ) : (
                  <span>{asset.name}</span>
                )}
                {asset.model && !isEditing && (
                  <span className="text-xs font-normal text-muted-foreground">({asset.model})</span>
                )}
              </h1>

              {isEditing ? (
                <input
                  type="text"
                  value={editForm.asset_code ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, asset_code: e.target.value })}
                  title="Asset ID — changing it also updates the barcode/QR tag"
                  className="h-7 w-32 px-2 font-mono text-xs font-semibold bg-background border border-input rounded text-foreground outline-none focus:ring-1 focus:ring-ring shrink-0"
                />
              ) : (
                <span className="font-mono text-xs font-semibold text-foreground px-1.5 py-0.5 rounded bg-secondary border border-border shrink-0">
                  {asset.asset_code}
                </span>
              )}
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-secondary text-muted-foreground border border-border shrink-0">
                {asset.category || asset.type || NONE}
              </span>

              {/* Status Pill */}
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold border shrink-0',
                  asset.status === 'Assigned'
                    ? 'bg-success/10 text-success-strong border-success/20'
                    : asset.status === 'In Stock'
                    ? 'bg-info/10 text-info-strong border-info/20'
                    : asset.status === 'Under Maintenance'
                    ? 'bg-warning/10 text-warning-strong border-warning/20'
                    : asset.status === 'Lost'
                    ? 'bg-destructive/10 text-destructive-strong border-destructive/20'
                    : 'bg-muted text-muted-foreground border-border'
                )}
              >
                <span className={cn('size-1.5 rounded-full', 
                  asset.status === 'Assigned' ? 'bg-success' :
                  asset.status === 'In Stock' ? 'bg-info' :
                  asset.status === 'Under Maintenance' ? 'bg-warning' :
                  asset.status === 'Lost' ? 'bg-destructive' : 'bg-muted-foreground'
                )} />
                {asset.status}
              </span>

              {openTicketsCount > 0 && (
                <button
                  onClick={() => setActiveTab('tickets')}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold bg-warning/10 text-warning-strong border border-warning/30 hover:bg-warning/20 transition-colors shrink-0"
                >
                  <AlertTriangle className="size-3" />
                  {openTicketsCount} Active {openTicketsCount === 1 ? 'Issue' : 'Issues'}
                </button>
              )}
            </div>
          </div>

          {/* Right: Quick Action Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors"
                >
                  <X className="size-3.5" /> Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
                >
                  <Save className="size-3.5" /> Save Changes
                </button>
              </>
            ) : (
              <>
                {/* Primary Button: Check-In or Check-Out per status */}
                {asset.status === 'Assigned' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setModalInput(EMPTY_MODAL);
                      setActiveModal('checkin');
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
                  >
                    <RotateCcw className="size-3.5" /> Check-In / Return
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setModalInput(EMPTY_MODAL);
                      setActiveModal('checkout');
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
                  >
                    <UserCheck className="size-3.5" /> Check-Out / Assign
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleStartEdit}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors"
                >
                  <Edit3 className="size-3.5" /> Edit Specs
                </button>

                {SHOW_BARCODE && !isCloudOrSoftware && (
                  <button
                    type="button"
                    onClick={() => setActiveModal('barcode')}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors"
                    title="Print ITAM Barcode Tag"
                  >
                    <Barcode className="size-3.5 text-primary" /> Print Barcode Tag
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleOpenTicketModal}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors"
                >
                  <ShieldAlert className="size-3.5" /> Raise Ticket
                </button>

                <button
                  type="button"
                  onClick={() => setActiveModal('retire')}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors"
                  title="Retire or decommission asset"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 2. MASTER-DETAIL TWO-COLUMN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* === LEFT COLUMN: PROFILE CARD STACK (3 of 12 cols = 25%) === */}
        <div className="lg:col-span-3 space-y-3.5">
          {isCloudOrSoftware ? (
            /* CLOUD & SOFTWARE SERVICE PROFILE CARD */
            <div className="rounded-lg border border-border bg-card p-3.5 shadow-xs space-y-3">
              {/* Service Identity & Status */}
              <div className="space-y-1.5 pb-2.5 border-b border-border/60">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary font-mono text-[10px] font-semibold">
                    <Cloud className="size-3" /> Cloud Service
                  </span>
                  <span className={cn(
                    'text-[10px] font-semibold px-2 py-0.5 rounded border',
                    asset.status === 'Assigned' || asset.status === 'Active' || asset.status === 'Running' ? 'bg-success/10 text-success-strong border-success/20' :
                    asset.status === 'In Stock' ? 'bg-info/10 text-info-strong border-info/20' :
                    asset.status === 'Under Maintenance' ? 'bg-warning/10 text-warning-strong border-warning/20' :
                    'bg-muted text-muted-foreground border-border'
                  )}>
                    {asset.status || 'Active'}
                  </span>
                </div>

                <div className="pt-0.5">
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-mono text-xs font-bold text-foreground">{asset.asset_code}</span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(asset.asset_code, 'Asset Code')}
                      className="text-xs text-muted-foreground hover:text-foreground p-1 rounded hover:bg-secondary transition-colors"
                      title="Copy Asset Code"
                    >
                      <Copy className="size-3" />
                    </button>
                  </div>
                  <h2 className="text-sm font-semibold font-display text-foreground leading-snug mt-0.5">
                    {asset.name}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {asset.model || asset.specifications?.appType || 'Project Management Microservice'}
                  </p>
                  <p className="text-[11px] text-muted-foreground/80 font-mono mt-0.5">
                    {asset.manufacturer || 'TeamOne Internal'}
                  </p>
                </div>
              </div>

              {/* Core Attributes: Domain, Hosting, Version */}
              <div className="space-y-2">
                {/* Domain */}
                <div className="rounded-md border border-border/70 bg-secondary/30 p-2.5 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium text-muted-foreground">
                      <Globe className="size-3.5 text-primary" /> Domain
                    </span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(digitalDetails.domain, 'Domain')}
                      className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-secondary transition-colors"
                      title="Copy Domain"
                    >
                      <Copy className="size-3" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-1 font-mono text-xs font-semibold text-foreground">
                    <span className="truncate" title={digitalDetails.domain}>{digitalDetails.domain}</span>
                    {digitalDetails.domain && (
                      <a
                        href={digitalDetails.domain.startsWith('http') ? digitalDetails.domain : `https://${digitalDetails.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:text-primary/80 shrink-0"
                        title="Open Domain"
                      >
                        <ExternalLink className="size-3" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Hosting */}
                <div className="rounded-md border border-border/70 bg-secondary/30 p-2.5 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium text-muted-foreground">
                      <Cloud className="size-3.5 text-primary" /> Hosting
                    </span>
                  </div>
                  <div className="font-semibold text-xs text-foreground truncate" title={digitalDetails.hosting}>
                    {digitalDetails.hosting}
                  </div>
                </div>

                {/* Version */}
                <div className="rounded-md border border-border/70 bg-secondary/30 p-2.5 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium text-muted-foreground">
                      <Tag className="size-3.5 text-primary" /> Version
                    </span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(digitalDetails.version, 'Version')}
                      className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-secondary transition-colors"
                      title="Copy Version"
                    >
                      <Copy className="size-3" />
                    </button>
                  </div>
                  <div className="font-mono font-semibold text-xs text-foreground">
                    <span className="px-1.5 py-0.5 rounded bg-secondary border border-border text-[11px]">
                      {digitalDetails.version}
                    </span>
                  </div>
                </div>
              </div>

              {/* Server Environments */}
              <div className="space-y-2 pt-2 border-t border-border/60">
                <span className="text-xs font-semibold text-foreground flex items-center gap-1.5 font-display">
                  <Server className="size-3.5 text-primary" /> Server Environments
                </span>

                {/* Dev Server */}
                <div className="rounded-md border border-border/70 bg-secondary/20 p-2 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium text-muted-foreground text-[11px]">
                      <span className="size-1.5 rounded-full bg-info animate-pulse" /> Dev Server
                    </span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(digitalDetails.devServer, 'Dev Server URL')}
                      className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-secondary transition-colors"
                      title="Copy Dev Server URL"
                    >
                      <Copy className="size-2.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-1 font-mono text-[11px] text-foreground">
                    <span className="truncate" title={digitalDetails.devServer}>{digitalDetails.devServer}</span>
                    {digitalDetails.devServer && (
                      <a
                        href={digitalDetails.devServer.startsWith('http') ? digitalDetails.devServer : `http://${digitalDetails.devServer}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:text-primary/80 shrink-0"
                        title="Open Dev Server"
                      >
                        <ExternalLink className="size-2.5" />
                      </a>
                    )}
                  </div>
                </div>

                {/* PR Server */}
                <div className="rounded-md border border-border/70 bg-secondary/20 p-2 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium text-muted-foreground text-[11px]">
                      <span className="size-1.5 rounded-full bg-warning" /> PR Server
                    </span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(digitalDetails.prServer, 'PR Server URL')}
                      className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-secondary transition-colors"
                      title="Copy PR Server URL"
                    >
                      <Copy className="size-2.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-1 font-mono text-[11px] text-foreground">
                    <span className="truncate" title={digitalDetails.prServer}>{digitalDetails.prServer}</span>
                    {digitalDetails.prServer && (
                      <a
                        href={digitalDetails.prServer.startsWith('http') ? digitalDetails.prServer : `https://${digitalDetails.prServer}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:text-primary/80 shrink-0"
                        title="Open PR Server"
                      >
                        <ExternalLink className="size-2.5" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Prod Server */}
                <div className="rounded-md border border-border/70 bg-secondary/20 p-2 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium text-muted-foreground text-[11px]">
                      <span className="size-1.5 rounded-full bg-success" /> Prod Server
                    </span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(digitalDetails.prodServer, 'Prod Server URL')}
                      className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-secondary transition-colors"
                      title="Copy Prod Server URL"
                    >
                      <Copy className="size-2.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-1 font-mono text-[11px] text-foreground">
                    <span className="truncate" title={digitalDetails.prodServer}>{digitalDetails.prodServer}</span>
                    {digitalDetails.prodServer && (
                      <a
                        href={digitalDetails.prodServer.startsWith('http') ? digitalDetails.prodServer : `https://${digitalDetails.prodServer}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:text-primary/80 shrink-0"
                        title="Open Prod Server"
                      >
                        <ExternalLink className="size-2.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="pt-2 border-t border-border/60">
                <button
                  type="button"
                  onClick={handleStartEdit}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors"
                >
                  <Edit3 className="size-3.5" /> Edit Cloud Specs
                </button>
              </div>
            </div>
          ) : (
            /* CARD 1: DEVICE PHOTO & BARCODE/QR PREVIEW (HARDWARE ASSETS) */
            <div className="rounded-lg border border-border bg-card p-3.5 shadow-xs space-y-2.5">
              <div className="relative aspect-[16/10] sm:aspect-video rounded-md border border-border bg-secondary/60 flex items-center justify-center overflow-hidden group">
                {primaryImage ? (
                  <img
                    src={primaryImage}
                    alt={asset.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                    onClick={() => setLightboxImage({ isOpen: true, url: primaryImage, index: 0 })}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-muted-foreground p-3 text-center">
                    <Laptop className="size-9 stroke-[1.25] text-muted-foreground/60 mb-1" />
                    <span className="text-xs font-medium text-muted-foreground">
                      No image available
                    </span>
                  </div>
                )}

                <div className="absolute top-2 right-2 flex items-center gap-1">
                  <span className="px-1.5 py-0.5 rounded bg-card/90 border border-border text-xs font-mono text-muted-foreground backdrop-blur-xs">
                    WebP / S3
                  </span>
                  {asset.images && asset.images.length > 0 && (
                    <button
                      onClick={() => setActiveTab('photos')}
                      className="px-1.5 py-0.5 rounded bg-card/90 border border-border text-xs font-semibold text-foreground backdrop-blur-xs hover:bg-secondary"
                    >
                      {asset.images.length} {asset.images.length === 1 ? 'photo' : 'photos'}
                    </button>
                  )}
                </div>

                {primaryImage && (
                  <button
                    onClick={() => setLightboxImage({ isOpen: true, url: primaryImage, index: 0 })}
                    className="absolute bottom-2 right-2 size-7 rounded bg-card/90 border border-border flex items-center justify-center text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Enlarge Photo"
                  >
                    <ZoomIn className="size-3.5" />
                  </button>
                )}
              </div>

              {/* Quick Upload / Paste Hint */}
              <div className="flex items-center justify-between pt-1 border-t border-border/40 text-xs">
                <span className="text-muted-foreground">Press <kbd className="px-1 py-0.2 rounded bg-secondary border border-border font-mono text-xs">Ctrl+V</kbd> to paste photo</span>
                <button
                  type="button"
                  onClick={() => {
                    setStagedPhotos([]);
                    setNewImageUrl('');
                    setImageUploadMode('file');
                    setShowAddImageModal(true);
                  }}
                  className="text-primary hover:underline font-semibold flex items-center gap-1"
                >
                  <Upload className="size-3" /> Upload
                </button>
              </div>

              {/* Asset Barcode & ITAM Tag Box */}
              <div className="rounded-md border border-border/60 bg-secondary/30 p-2.5 space-y-2">
                {SHOW_BARCODE && (<>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground flex items-center gap-1.5 font-display">
                    <Barcode className="size-3.5 text-primary" /> Asset Barcode Tag
                  </span>
                  <div className="flex items-center gap-1">
                    {/* Mode switcher: Barcode (default) / QR Code */}
                    <div className="flex items-center rounded border border-border bg-card p-0.5 text-[10px]">
                      <button
                        type="button"
                        onClick={() => setTagMode('barcode')}
                        className={cn(
                          'px-1.5 py-0.5 rounded font-medium transition-colors flex items-center gap-1',
                          tagMode === 'barcode' ? 'bg-secondary text-foreground font-semibold shadow-2xs' : 'text-muted-foreground hover:text-foreground'
                        )}
                        title="Switch to 1D Industrial Barcode"
                      >
                        <Barcode className="size-2.5" /> Barcode
                      </button>
                      <button
                        type="button"
                        onClick={() => setTagMode('qr')}
                        className={cn(
                          'px-1.5 py-0.5 rounded font-medium transition-colors flex items-center gap-1',
                          tagMode === 'qr' ? 'bg-secondary text-foreground font-semibold shadow-2xs' : 'text-muted-foreground hover:text-foreground'
                        )}
                        title="Switch to 2D QR Code"
                      >
                        <QrCode className="size-2.5" /> QR
                      </button>
                    </div>

                    {/* Download button */}
                    {(tagMode === 'barcode' ? barcodeDataUrl : qrCodeDataUrl) && (
                      <a
                        href={tagMode === 'barcode' ? barcodeDataUrl : qrCodeDataUrl}
                        download={`${asset.asset_code}_${tagMode === 'barcode' ? 'Barcode' : 'QR'}.png`}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 p-1 rounded hover:bg-secondary transition-colors"
                        title={`Download ${tagMode === 'barcode' ? 'Barcode' : 'QR Code'} PNG`}
                      >
                        <Download className="size-3" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => copyToClipboard(asset.asset_code, 'Asset Code')}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 font-mono p-1 rounded hover:bg-secondary transition-colors"
                      title="Copy Asset Code"
                    >
                      <Copy className="size-3" />
                    </button>
                  </div>
                </div>

                {/* Visual Asset Tag Card */}
                <div className="py-2.5 px-3 bg-card rounded border border-border flex flex-col items-center justify-center text-center space-y-1.5 shadow-2xs">
                  <div className="flex items-center justify-between w-full border-b border-border/60 pb-1 text-[10px]">
                    <span className="font-bold text-primary tracking-wider uppercase">{(asset as any)?.company || 'TEAMONE'}</span>
                    <span className="text-muted-foreground text-[9px] uppercase tracking-wider font-semibold">IT Asset Tag</span>
                  </div>

                  {tagMode === 'barcode' ? (
                    <div className="w-full flex flex-col items-center justify-center py-1 bg-white rounded border border-border/60 my-0.5 overflow-hidden">
                      <svg ref={barcodeSvgRef} className="max-w-full h-auto" />
                    </div>
                  ) : (
                    qrCodeDataUrl ? (
                      <img
                        src={qrCodeDataUrl}
                        alt={`QR Code for ${asset.asset_code}`}
                        className="size-24 p-1 bg-white rounded border border-border/60 object-contain shadow-2xs my-0.5"
                      />
                    ) : (
                      <div className="size-24 flex items-center justify-center bg-secondary/40 rounded border border-border/60 text-xs text-muted-foreground my-0.5">
                        Generating QR...
                      </div>
                    )
                  )}

                  {/* Dynamic Asset Info under Barcode */}
                  <div className="w-full text-left pt-0.5 space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-bold text-foreground">{asset.asset_code}</span>
                      <span className={cn(
                        'text-[10px] font-semibold px-1.5 py-0.2 rounded border',
                        asset.status === 'Assigned' ? 'bg-success/10 text-success-strong border-success/20' :
                        asset.status === 'In Stock' ? 'bg-info/10 text-info-strong border-info/20' :
                        asset.status === 'Under Maintenance' ? 'bg-warning/10 text-warning-strong border-warning/20' :
                        'bg-muted text-muted-foreground border-border'
                      )}>
                        {asset.status}
                      </span>
                    </div>
                    <div className="text-[11px] font-semibold text-foreground truncate max-w-full">
                      {asset.name}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate max-w-full font-mono">
                      {asset.manufacturer || asset.model ? `${asset.manufacturer || ''}${asset.manufacturer && asset.model ? ' · ' : ''}${asset.model || ''}` : NONE}
                    </div>
                  </div>
                </div>
                </>)}

                {/* Quick Navigation Strip */}
                <div className="pt-2 border-t border-border/50 grid grid-cols-2 gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setActiveTab('custody')}
                    className="flex flex-col p-2 rounded-md bg-secondary/30 hover:bg-secondary/60 border border-border/60 transition-colors text-left"
                  >
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <UserCheck className="size-3 text-primary" /> Custody
                    </span>
                    <span className="font-semibold text-foreground truncate mt-0.5">
                      {asset.status === 'Assigned' ? (asset.assignedToName || 'Assigned') : 'In Stock'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('procurement')}
                    className="flex flex-col p-2 rounded-md bg-secondary/30 hover:bg-secondary/60 border border-border/60 transition-colors text-left"
                  >
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <ShieldCheck className="size-3 text-primary" /> Warranty
                    </span>
                    <span className={cn('font-semibold truncate mt-0.5',
                      warrantyDays && warrantyDays > 0 ? 'text-success-strong' : 'text-destructive'
                    )}>
                      {warrantyDays && warrantyDays > 0 ? `${warrantyDays}d left` : 'Expired'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* === RIGHT COLUMN: TABBED ITAM OPERATIONS WORKSPACE (9 of 12 cols = 75%) === */}
        <div className="lg:col-span-9 space-y-3.5">
          {/* TAB SELECTION HEADER */}
          <div className="rounded-lg border border-border bg-card shadow-xs">
            <div className="flex items-center border-b border-border px-3 gap-1.5 overflow-x-auto">
              <button
                type="button"
                onClick={() => setActiveTab('specs')}
                className={cn(
                  'inline-flex items-center gap-1.5 py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap',
                  activeTab === 'specs'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {isCloudOrSoftware ? (
                  <><Code2 className="size-3.5" /> Service &amp; Cloud Specs</>
                ) : (
                  <><Cpu className="size-3.5" /> Hardware Specs</>
                )}
              </button>

              {!isCloudOrSoftware && (
                <button
                  type="button"
                  onClick={() => setActiveTab('custody')}
                  className={cn(
                    'inline-flex items-center gap-1.5 py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap',
                    activeTab === 'custody'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <UserCheck className="size-3.5" /> Custody &amp; Location
                  <span className={cn('text-xs px-1.5 py-0.2 rounded-full font-mono', 
                    asset.status === 'Assigned' ? 'bg-success/20 text-success-strong font-semibold' : 'bg-secondary text-muted-foreground'
                  )}>
                    {asset.status === 'Assigned' ? (asset.assignedToName || 'Assigned') : 'In Stock'}
                  </span>
                </button>
              )}

              {!isCloudOrSoftware && (
                <button
                  type="button"
                  onClick={() => setActiveTab('procurement')}
                  className={cn(
                    'inline-flex items-center gap-1.5 py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap',
                    activeTab === 'procurement' || activeTab === 'documents'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <FileText className="size-3.5" /> Procurement &amp; Documents
                  <span className="text-xs px-1.5 py-0.2 rounded-full font-mono bg-secondary text-muted-foreground">
                    {(asset.invoices?.length || 0) + (asset.attachments?.length || 0)}
                  </span>
                  {warrantyDays && warrantyDays > 0 ? (
                    <span className="text-xs px-1.5 py-0.2 rounded-full font-mono bg-success/20 text-success-strong font-semibold">
                      {warrantyDays}d
                    </span>
                  ) : null}
                </button>
              )}

              <button
                type="button"
                onClick={() => setActiveTab('tickets')}
                className={cn(
                  'inline-flex items-center gap-1.5 py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap',
                  activeTab === 'tickets'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <ShieldAlert className="size-3.5" /> Support Tickets
                <span className={cn('text-xs px-1.5 py-0.2 rounded-full font-mono', 
                  openTicketsCount > 0 ? 'bg-warning/20 text-warning-strong font-semibold' : 'bg-secondary text-muted-foreground'
                )}>
                  {linkedTickets.length}
                </span>
              </button>

              {!isCloudOrSoftware && (
                <button
                  type="button"
                  onClick={() => setActiveTab('photos')}
                  className={cn(
                    'inline-flex items-center gap-1.5 py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap',
                    activeTab === 'photos'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <ImageIcon className="size-3.5" /> Photo Vault
                  <span className="text-xs px-1.5 py-0.2 rounded-full font-mono bg-secondary text-muted-foreground">
                    {asset.images?.length || 0}
                  </span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={cn(
                  'inline-flex items-center gap-1.5 py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap',
                  activeTab === 'history'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Clock className="size-3.5" /> Activity &amp; Audit
              </button>
            </div>

            {/* TAB CONTENT CONTAINER */}
            <div className="p-3.5">
              {/* TAB 1: SPECS & DEPRECIATION */}
              {activeTab === 'specs' && (
                <div className="space-y-3.5">
                  {isCloudOrSoftware ? (
                    <>
                      {/* 1. CLOUD OPERATIONAL DETAILS: DOMAIN, HOSTING, VERSION, CLUSTER */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <Globe className="size-3.5 text-primary" /> Domain, Hosting &amp; Deployment Configuration
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-2">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Domain / URL:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.domain !== undefined ? editForm.domain : (digitalDetails.domain || '')}
                                  onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                                  placeholder="e.g. work.teamone.internal"
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground font-mono text-right w-56"
                                />
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <strong className="text-foreground font-mono">{digitalDetails.domain || NONE}</strong>
                                  {digitalDetails.domain && digitalDetails.domain !== NONE && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(digitalDetails.domain, 'Domain')}
                                        className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-secondary"
                                        title="Copy Domain"
                                      >
                                        <Copy className="size-3" />
                                      </button>
                                      <a
                                        href={digitalDetails.domain.startsWith('http') ? digitalDetails.domain : `https://${digitalDetails.domain}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary hover:text-primary/80"
                                        title="Open Domain"
                                      >
                                        <ExternalLink className="size-3" />
                                      </a>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Hosting Infrastructure:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.hosting !== undefined ? editForm.hosting : (digitalDetails.hosting || '')}
                                  onChange={(e) => setEditForm({ ...editForm, hosting: e.target.value, hostingPlatform: e.target.value })}
                                  placeholder="e.g. AWS Cloud / Node.js Microservice"
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right w-56"
                                />
                              ) : (
                                <strong className="text-foreground flex items-center gap-1">
                                  <Cloud className="size-3 text-primary" /> {digitalDetails.hosting || NONE}
                                </strong>
                              )}
                            </div>

                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Version / Release:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.version !== undefined ? editForm.version : (digitalDetails.version || '')}
                                  onChange={(e) => setEditForm({ ...editForm, version: e.target.value })}
                                  placeholder="e.g. v1.4.0"
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground font-mono text-right w-40"
                                />
                              ) : (
                                <div className="flex items-center gap-1">
                                  <span className="px-1.5 py-0.5 rounded bg-secondary border border-border text-[11px] font-mono font-semibold">
                                    {digitalDetails.version || NONE}
                                  </span>
                                  {digitalDetails.version && digitalDetails.version !== NONE && (
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(digitalDetails.version, 'Version')}
                                      className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-secondary"
                                      title="Copy Version"
                                    >
                                      <Copy className="size-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-2">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Environment Stage:</span>
                              {isEditing ? (
                                <select
                                  value={editForm.environment || 'Production'}
                                  onChange={(e) => setEditForm({ ...editForm, environment: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right cursor-pointer"
                                >
                                  <option value="Production">Production</option>
                                  <option value="Staging">Staging</option>
                                  <option value="Development">Development</option>
                                  <option value="Testing">Testing</option>
                                </select>
                              ) : (
                                <span className={cn(
                                  'text-xs font-semibold px-2 py-0.5 rounded border',
                                  (asset.specifications?.environment || 'Production') === 'Production'
                                    ? 'bg-success/10 text-success-strong border-success/20'
                                    : 'bg-info/10 text-info-strong border-info/20'
                                )}>
                                  {asset.specifications?.environment || 'Production'}
                                </span>
                              )}
                            </div>

                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Cluster Region / Datacenter:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.clusterRegion || ''}
                                  onChange={(e) => setEditForm({ ...editForm, clusterRegion: e.target.value })}
                                  placeholder="e.g. us-east-1 (N. Virginia)"
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right w-48"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.specifications?.clusterRegion || 'Global / AWS Cloud'}</strong>
                              )}
                            </div>

                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Service Module / App:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.service || ''}
                                  onChange={(e) => setEditForm({ ...editForm, service: e.target.value })}
                                  placeholder="e.g. work-service"
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground font-mono text-right w-48"
                                />
                              ) : (
                                <strong className="text-foreground font-mono">{asset.specifications?.service || asset.name}</strong>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 2. SERVER ENVIRONMENTS: DEV SERVER, PR SERVER, PROD SERVER */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <Server className="size-3.5 text-primary" /> Active Server Environments (Dev · PR · Prod)
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                          {/* Dev Server Card */}
                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="flex items-center gap-1.5 font-semibold text-foreground">
                                <span className="size-2 rounded-full bg-info animate-pulse" /> Dev Server
                              </span>
                              <span className="text-[10px] px-1.5 py-0.2 rounded bg-info/10 text-info-strong border border-info/20 font-medium">
                                Development
                              </span>
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.devServer !== undefined ? editForm.devServer : (digitalDetails.devServer || '')}
                                onChange={(e) => setEditForm({ ...editForm, devServer: e.target.value })}
                                placeholder="e.g. http://127.0.0.1:3004/api"
                                className="h-6 w-full px-2 bg-background border border-input rounded text-xs text-foreground font-mono"
                              />
                            ) : (
                              <div className="pt-1 space-y-1">
                                <div className="font-mono text-xs font-semibold text-foreground break-all">
                                  {digitalDetails.devServer || NONE}
                                </div>
                                {digitalDetails.devServer && digitalDetails.devServer !== NONE && (
                                  <div className="flex items-center gap-2 pt-0.5">
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(digitalDetails.devServer, 'Dev Server URL')}
                                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground font-medium"
                                    >
                                      <Copy className="size-3" /> Copy URL
                                    </button>
                                    <a
                                      href={digitalDetails.devServer.startsWith('http') ? digitalDetails.devServer : `http://${digitalDetails.devServer}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                                    >
                                      <ExternalLink className="size-3" /> Launch
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* PR Server Card */}
                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="flex items-center gap-1.5 font-semibold text-foreground">
                                <span className="size-2 rounded-full bg-warning" /> PR Server
                              </span>
                              <span className="text-[10px] px-1.5 py-0.2 rounded bg-warning/10 text-warning-strong border border-warning/20 font-medium">
                                PR / Staging
                              </span>
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.prServer !== undefined ? editForm.prServer : (digitalDetails.prServer || '')}
                                onChange={(e) => setEditForm({ ...editForm, prServer: e.target.value })}
                                placeholder="e.g. https://pr-work.teamone.internal"
                                className="h-6 w-full px-2 bg-background border border-input rounded text-xs text-foreground font-mono"
                              />
                            ) : (
                              <div className="pt-1 space-y-1">
                                <div className="font-mono text-xs font-semibold text-foreground break-all">
                                  {digitalDetails.prServer || NONE}
                                </div>
                                {digitalDetails.prServer && digitalDetails.prServer !== NONE && (
                                  <div className="flex items-center gap-2 pt-0.5">
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(digitalDetails.prServer, 'PR Server URL')}
                                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground font-medium"
                                    >
                                      <Copy className="size-3" /> Copy URL
                                    </button>
                                    <a
                                      href={digitalDetails.prServer.startsWith('http') ? digitalDetails.prServer : `https://${digitalDetails.prServer}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                                    >
                                      <ExternalLink className="size-3" /> Launch
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Prod Server Card */}
                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="flex items-center gap-1.5 font-semibold text-foreground">
                                <span className="size-2 rounded-full bg-success" /> Prod Server
                              </span>
                              <span className="text-[10px] px-1.5 py-0.2 rounded bg-success/10 text-success-strong border border-success/20 font-medium">
                                Production Live
                              </span>
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.prodServer !== undefined ? editForm.prodServer : (digitalDetails.prodServer || '')}
                                onChange={(e) => setEditForm({ ...editForm, prodServer: e.target.value })}
                                placeholder="e.g. http://127.0.0.1:3001/api/work"
                                className="h-6 w-full px-2 bg-background border border-input rounded text-xs text-foreground font-mono"
                              />
                            ) : (
                              <div className="pt-1 space-y-1">
                                <div className="font-mono text-xs font-semibold text-foreground break-all">
                                  {digitalDetails.prodServer || NONE}
                                </div>
                                {digitalDetails.prodServer && digitalDetails.prodServer !== NONE && (
                                  <div className="flex items-center gap-2 pt-0.5">
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(digitalDetails.prodServer, 'Prod Server URL')}
                                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground font-medium"
                                    >
                                      <Copy className="size-3" /> Copy URL
                                    </button>
                                    <a
                                      href={digitalDetails.prodServer.startsWith('http') ? digitalDetails.prodServer : `https://${digitalDetails.prodServer}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                                    >
                                      <ExternalLink className="size-3" /> Launch
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 3. SOFTWARE ARCHITECTURE & RUNTIME SPECS */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <Code2 className="size-3.5 text-primary" /> Application Architecture &amp; Service Runtime
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-1.5">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">App Type / Tech Stack:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.appType !== undefined ? editForm.appType : (editForm.model || '')}
                                  onChange={(e) => setEditForm({ ...editForm, appType: e.target.value, model: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.specifications?.appType || asset.model || NONE}</strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Component / Module:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.component || ''}
                                  onChange={(e) => setEditForm({ ...editForm, component: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.specifications?.component || NONE}</strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Source Repository:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.repoUrl || ''}
                                  onChange={(e) => setEditForm({ ...editForm, repoUrl: e.target.value })}
                                  placeholder="https://github.com/..."
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground font-mono text-right w-56"
                                />
                              ) : (
                                asset.specifications?.repoUrl ? (
                                  <a
                                    href={asset.specifications.repoUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary hover:underline font-mono flex items-center gap-1"
                                  >
                                    <GitBranch className="size-3" /> {asset.specifications.repoUrl.replace(/^https?:\/\//, '')}
                                  </a>
                                ) : (
                                  <span className="text-muted-foreground font-mono">{NONE}</span>
                                )
                              )}
                            </div>
                          </div>

                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-1.5">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Runtime / Container:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.runtime || ''}
                                  onChange={(e) => setEditForm({ ...editForm, runtime: e.target.value })}
                                  placeholder="e.g. Node 20 / Docker Alpine"
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground font-mono">{asset.specifications?.runtime || asset.specifications?.containerImage || 'Node.js 20 LTS'}</strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Service Port / Protocol:</span>
                              {isEditing ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={editForm.servicePort || ''}
                                    onChange={(e) => setEditForm({ ...editForm, servicePort: e.target.value })}
                                    placeholder="Port"
                                    className="h-6 w-16 px-1.5 bg-background border border-input rounded text-xs text-foreground font-mono text-right"
                                  />
                                  <input
                                    type="text"
                                    value={editForm.protocol || ''}
                                    onChange={(e) => setEditForm({ ...editForm, protocol: e.target.value })}
                                    placeholder="HTTP/HTTPS"
                                    className="h-6 w-20 px-1.5 bg-background border border-input rounded text-xs text-foreground text-right"
                                  />
                                </div>
                              ) : (
                                <strong className="text-foreground font-mono">
                                  {asset.specifications?.servicePort ? `${asset.specifications.servicePort} (${asset.specifications?.protocol || 'HTTP'})` : '3004 (HTTP/REST)'}
                                </strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Health Check URL:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.healthCheckUrl || ''}
                                  onChange={(e) => setEditForm({ ...editForm, healthCheckUrl: e.target.value })}
                                  placeholder="/health"
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground font-mono text-right w-48"
                                />
                              ) : (
                                <strong className="text-foreground font-mono">{asset.specifications?.healthCheckUrl || '/health'}</strong>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 4. OPERATIONAL NOTES & REMARKS */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <Info className="size-3.5 text-muted-foreground" /> Operational Notes &amp; Runbook
                        </h3>
                        {isEditing ? (
                          <textarea
                            rows={2}
                            value={editForm.remarks || editForm.notes || ''}
                            onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                            className="w-full p-2 bg-background border border-input rounded text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                            placeholder="Add deployment instructions, service notes, or runbook links..."
                          />
                        ) : (
                          <div className="p-2.5 rounded-md bg-secondary/30 border border-border/60 text-xs text-muted-foreground leading-relaxed">
                            {asset.remarks || asset.notes || 'No remarks recorded.'}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* System Architecture & Compute Specs */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <Cpu className="size-3.5 text-muted-foreground" /> System &amp; Processing Specs
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-1.5">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Operating System:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.os || ''}
                                  onChange={(e) => setEditForm({ ...editForm, os: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.os || NONE}</strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">RAM / System Memory:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.ram || ''}
                                  onChange={(e) => setEditForm({ ...editForm, ram: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.ram || NONE}</strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Internal Storage:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.storage || ''}
                                  onChange={(e) => setEditForm({ ...editForm, storage: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.storage || NONE}</strong>
                              )}
                            </div>
                          </div>

                          <div className="rounded-md border border-border bg-secondary/20 p-2.5 space-y-1.5">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Processor Speed:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.cpuSpeed || ''}
                                  onChange={(e) => setEditForm({ ...editForm, cpuSpeed: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.cpuSpeed || NONE}</strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">CPU Cores:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.cpuCoreCount || ''}
                                  onChange={(e) => setEditForm({ ...editForm, cpuCoreCount: e.target.value })}
                                  className="h-6 px-2 bg-background border border-input rounded text-xs text-foreground text-right"
                                />
                              ) : (
                                <strong className="text-foreground">{asset.specifications.cpuCoreCount || NONE}</strong>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Asset Classification:</span>
                              <span className="font-semibold text-foreground">{asset.category || asset.type || NONE}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Network Configuration */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <Network className="size-3.5 text-muted-foreground" /> Network Interfaces &amp; Addressing
                        </h3>
                        <div className="rounded-md border border-border bg-secondary/20 p-2.5">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 text-xs">
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">Primary MAC Address:</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.macAddress || ''}
                                  onChange={(e) => setEditForm({ ...editForm, macAddress: e.target.value })}
                                  className="h-6 w-full px-2 bg-background border border-input rounded font-mono text-xs text-foreground"
                                />
                              ) : (
                                <span className="font-mono font-semibold text-foreground">
                                  {asset.macAddress || NONE}
                                </span>
                              )}
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">IP Address (DHCP / Static):</span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.ipAddress || ''}
                                  onChange={(e) => setEditForm({ ...editForm, ipAddress: e.target.value })}
                                  className="h-6 w-full px-2 bg-background border border-input rounded font-mono text-xs text-foreground"
                                />
                              ) : (
                                <span className="font-mono font-semibold text-foreground">
                                  {asset.ipAddress || NONE}
                                </span>
                              )}
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">OS version:</span>
                              <span className="font-mono text-foreground">
                                {asset.osVersion || NONE}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Financial Straight-Line Depreciation Model */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <DollarSign className="size-3.5 text-muted-foreground" /> Enterprise Depreciation Schedule
                        </h3>
                        <div className="rounded-md border border-border bg-secondary/20 p-2.5">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">Purchase Cost</span>
                              <span className="font-mono font-semibold text-foreground">
                                {money(asset.purchaseCost)}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">Salvage Value</span>
                              <span className="font-mono font-semibold text-foreground">
                                {money(salvageValue)}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">Depreciation Method</span>
                              <span className="font-semibold text-foreground">{depreciationPolicy ? `${depreciationPolicy.method}${usefulLifeYears ? ` (${usefulLifeYears}-yr)` : ''}` : NONE}</span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">Estimated Monthly</span>
                              <span className="font-mono font-semibold text-foreground">
                                {monthlyDepreciation === null ? NONE : `${money(monthlyDepreciation)} / mo`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Operational Notes */}
                      <div>
                        <h3 className="text-foreground flex items-center gap-1.5 mb-1.5 font-display text-card-title">
                          <Info className="size-3.5 text-muted-foreground" /> Administrative Remarks
                        </h3>
                        {isEditing ? (
                          <textarea
                            rows={2}
                            value={editForm.remarks || editForm.notes || ''}
                            onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                            className="w-full p-2 bg-background border border-input rounded text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                            placeholder="Add asset notes or comments..."
                          />
                        ) : (
                          <div className="p-2.5 rounded-md bg-secondary/30 border border-border/60 text-xs text-muted-foreground leading-relaxed">
                            {asset.remarks || asset.notes || 'No remarks recorded.'}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* TAB 2: CUSTODY & PHYSICAL LOCATION */}
              {!isCloudOrSoftware && activeTab === 'custody' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-3">
                    <div>
                      <h3 className="text-foreground flex items-center gap-1.5 font-display text-card-title">
                        <UserCheck className="size-3.5 text-muted-foreground" /> Custody &amp; Physical Location
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Track employee custody, assignment history, and site deployment coordinates.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {asset.status === 'Assigned' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setModalInput({ ...EMPTY_MODAL, department: asset.department || '', site: asset.site || '' });
                              setActiveModal('checkout');
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card text-foreground text-xs font-semibold hover:bg-secondary transition-colors"
                          >
                            <UserCheck className="size-3.5 text-primary" /> Reassign / Transfer
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setModalInput(EMPTY_MODAL);
                              setActiveModal('checkin');
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
                          >
                            <RotateCcw className="size-3.5" /> Check-In / Return
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setModalInput(EMPTY_MODAL);
                            setActiveModal('checkout');
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
                        >
                          <UserCheck className="size-3.5" /> Deploy to Employee
                        </button>
                      )}
                    </div>
                  </div>

                  {asset.status === 'Assigned' ? (
                    <div className="space-y-4">
                      {/* Assignee Profile Banner */}
                      <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="size-11 rounded-full bg-secondary border border-border flex items-center justify-center font-bold text-sm text-foreground shrink-0 shadow-2xs">
                              {asset.assignedToName ? asset.assignedToName.substring(0, 2).toUpperCase() : '—'}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-foreground">
                                  {asset.assignedToName || NONE}
                                </span>
                                {asset.assignee?.code && (
                                  <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-secondary border border-border text-muted-foreground">
                                    {asset.assignee.code}
                                  </span>
                                )}
                                <span className="text-xs px-2 py-0.5 rounded-full bg-success/10 text-success-strong border border-success/20 font-semibold">
                                  Active Custodian
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground block mt-0.5">
                                {[asset.assignee?.designation, asset.assignee?.department || asset.department].filter(Boolean).join(' · ') || NONE}
                              </span>
                            </div>
                          </div>

                          {asset.assignedTo && (
                            <Link
                              to={`/people/employees/${asset.assignedTo}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-border bg-card text-xs font-semibold text-primary hover:bg-secondary transition-colors shrink-0"
                            >
                              <User className="size-3.5" /> Employee Profile <ArrowUpRight className="size-3" />
                            </Link>
                          )}
                        </div>

                        {/* Assignee Contact Grid */}
                        {asset.assignee && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 pt-2 border-t border-border/60 text-xs">
                            <div className="p-2.5 rounded bg-card border border-border/60">
                              <span className="text-muted-foreground block text-xs">Email Address</span>
                              <span className="font-semibold text-foreground truncate block mt-0.5">
                                {asset.assignee.email || asset.assignedToEmail || NONE}
                              </span>
                            </div>
                            <div className="p-2.5 rounded bg-card border border-border/60">
                              <span className="text-muted-foreground block text-xs">Phone Number</span>
                              <span className="font-semibold text-foreground truncate block mt-0.5">
                                {asset.assignee.phone || NONE}
                              </span>
                            </div>
                            <div className="p-2.5 rounded bg-card border border-border/60">
                              <span className="text-muted-foreground block text-xs">Reports To (Manager)</span>
                              <span className="font-semibold text-foreground truncate block mt-0.5">
                                {asset.assignee.managerName || NONE}
                              </span>
                            </div>
                            <div className="p-2.5 rounded bg-card border border-border/60">
                              <span className="text-muted-foreground block text-xs">Work Location</span>
                              <span className="font-semibold text-foreground truncate block mt-0.5">
                                {asset.assignee.location || NONE}
                              </span>
                            </div>
                          </div>
                        )}

                        {(asset.assignee?.status === 'terminated' || asset.assignee?.status === 'resigned') && (
                          <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive-strong text-xs flex items-center gap-2">
                            <AlertTriangle className="size-4 shrink-0" />
                            <span>This employee has left the organization ({asset.assignee?.status}). Immediate custody recovery is required.</span>
                          </div>
                        )}
                      </div>

                      {/* Physical Location & Checkout Details */}
                      <div>
                        <h4 className="text-foreground flex items-center gap-1.5 mb-2 font-display text-card-title">
                          <MapPin className="size-3.5 text-muted-foreground" /> Physical Placement Coordinates
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                          <div className="rounded-lg border border-border bg-card p-3 space-y-1">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Calendar className="size-3.5 text-primary" />
                              <span>Checked Out Date</span>
                            </div>
                            <p className="text-sm font-semibold text-foreground pt-1">
                              {asset.assignedOn || NONE}
                            </p>
                          </div>
                          <div className="rounded-lg border border-border bg-card p-3 space-y-1">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Building className="size-3.5 text-primary" />
                              <span>Office / Site Facility</span>
                            </div>
                            <p className="text-sm font-semibold text-foreground pt-1">
                              {asset.site || NONE}
                            </p>
                          </div>
                          <div className="rounded-lg border border-border bg-card p-3 space-y-1">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <MapPin className="size-3.5 text-primary" />
                              <span>Desk / Room / Floor</span>
                            </div>
                            <p className="text-sm font-semibold text-foreground pt-1">
                              {asset.location || NONE}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 py-2">
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-secondary/30 border border-border">
                        <CheckCircle2 className="size-5 text-success shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <h4 className="font-semibold text-foreground text-sm">In Stock &amp; Ready for Deployment</h4>
                          <p className="text-xs text-muted-foreground">
                            This asset is currently stored in company inventory and available to be checked out to an employee or designated team.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div className="rounded-lg border border-border bg-card p-3.5 space-y-1.5">
                          <span className="text-muted-foreground flex items-center gap-1.5">
                            <MapPin className="size-3.5 text-primary" /> Storage Room / Locker:
                          </span>
                          <span className="text-sm font-semibold text-foreground block">
                            {asset.location || NONE}
                          </span>
                        </div>
                        <div className="rounded-lg border border-border bg-card p-3.5 space-y-1.5">
                          <span className="text-muted-foreground flex items-center gap-1.5">
                            <Building className="size-3.5 text-primary" /> Facility / Site:
                          </span>
                          <span className="text-sm font-semibold text-foreground block">
                            {asset.site || NONE}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 3: PROCUREMENT, WARRANTY & ATTACHED DOCUMENTS (SINGLE MERGED TAB) */}
              {!isCloudOrSoftware && (activeTab === 'procurement' || activeTab === 'documents') && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-3">
                    <div>
                      <h3 className="text-foreground flex items-center gap-1.5 font-display text-card-title">
                        <FileText className="size-3.5 text-muted-foreground" /> Procurement, Warranty &amp; Documents
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Acquisition details, PO records, vendor warranty expiration and attached commercial documents.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenAddDocModal('invoice')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs cursor-pointer"
                      >
                        <Plus className="size-3.5" /> Upload Invoice
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenAddDocModal('warranty')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-secondary text-foreground text-xs font-semibold transition-colors cursor-pointer"
                      >
                        <Plus className="size-3.5" /> Add Certificate
                      </button>
                    </div>
                  </div>

                  {/* Financial & Acquisition Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 text-xs">
                    <div className="p-3 rounded-lg border border-border bg-card space-y-1">
                      <span className="text-muted-foreground block text-xs">Ownership</span>
                      {isEditing ? (
                        <select
                          value={editForm.ownership || 'owned'}
                          onChange={(e) => setEditForm({ ...editForm, ownership: e.target.value })}
                          className="w-full h-7 px-1.5 bg-background border border-input rounded text-xs font-semibold text-foreground outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="owned">Owned</option>
                          <option value="rental">Rental</option>
                        </select>
                      ) : (
                        <span className="font-semibold text-foreground block truncate">
                          {ownershipLabel(asset.ownership)}
                        </span>
                      )}
                    </div>
                    <div className="p-3 rounded-lg border border-border bg-card space-y-1">
                      <span className="text-muted-foreground block text-xs">Acquisition Type</span>
                      <span className="font-semibold text-foreground capitalize block truncate">
                        {asset.specifications.acquisitionType || NONE}
                      </span>
                    </div>
                    <div className="p-3 rounded-lg border border-border bg-card space-y-1">
                      <span className="text-muted-foreground block text-xs">Purchase Cost</span>
                      <span className="font-mono font-semibold text-foreground block truncate">
                        {asset.purchaseCost !== null && asset.purchaseCost !== undefined ? money(asset.purchaseCost) : NONE}
                      </span>
                    </div>
                    <div className="p-3 rounded-lg border border-border bg-card space-y-1">
                      <span className="text-muted-foreground block text-xs">Vendor / Supplier</span>
                      {asset.vendor && asset.vendor !== NONE ? (
                        <Link
                          to={`/assets/vendors?search=${encodeURIComponent(asset.vendor)}`}
                          className="font-semibold text-primary hover:underline truncate flex items-center gap-1"
                          title="View in Vendors Directory"
                        >
                          <Truck className="size-3.5 shrink-0" />
                          <span className="truncate">{asset.vendor}</span>
                        </Link>
                      ) : (
                        <span className="font-semibold text-foreground block truncate">
                          {NONE}
                        </span>
                      )}
                    </div>
                    <div className="p-3 rounded-lg border border-border bg-card space-y-1">
                      <span className="text-muted-foreground block text-xs">Purchase Date</span>
                      <span className="font-semibold text-foreground block truncate">
                        {asset.purchaseDate || NONE}
                      </span>
                    </div>
                    <div className="p-3 rounded-lg border border-border bg-card space-y-1">
                      <span className="text-muted-foreground block text-xs">PO Number</span>
                      <span className="font-mono font-semibold text-foreground block truncate">
                        {asset.poNumber || NONE}
                      </span>
                    </div>
                    <div className="p-3 rounded-lg border border-border bg-card space-y-1">
                      <span className="text-muted-foreground block text-xs">Invoice Number</span>
                      <span className="font-mono font-semibold text-foreground block truncate">
                        {asset.invoiceNumber || NONE}
                      </span>
                    </div>
                  </div>

                  {/* Warranty Coverage Status */}
                  <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground text-sm">OEM &amp; Vendor Warranty Protection</span>
                          <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full border',
                            warrantyDays && warrantyDays > 0 
                              ? 'bg-success/10 text-success-strong border-success/20'
                              : asset.warrantyUntil
                              ? 'bg-destructive/10 text-destructive border-destructive/20'
                              : 'bg-secondary text-muted-foreground border-border'
                          )}>
                            {warrantyDays && warrantyDays > 0 
                              ? `Active · ${warrantyDays} Days Remaining` 
                              : asset.warrantyUntil
                              ? 'Warranty Expired'
                              : 'No Warranty Recorded'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Valid until: <strong className="text-foreground">{asset.warrantyUntil || NONE}</strong> · Coverage includes hardware repairs, component replacements, and OEM support.
                        </p>
                      </div>

                      {/* Document shortcuts */}
                      <div className="flex items-center gap-2 shrink-0">
                        {asset.invoices.length > 0 && (
                          <button
                            type="button"
                            onClick={() => handleOpenInvoiceDoc()}
                            className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md border border-border bg-card hover:bg-secondary text-xs font-semibold text-foreground transition-colors cursor-pointer"
                          >
                            <FileText className="size-3.5 text-primary" /> View Invoice
                          </button>
                        )}
                        {asset.attachments.some((a) => a.type === 'warranty') && (
                          <button
                            type="button"
                            onClick={() => handleOpenWarrantyDoc()}
                            className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md border border-border bg-card hover:bg-secondary text-xs font-semibold text-foreground transition-colors cursor-pointer"
                          >
                            <ShieldCheck className="size-3.5 text-primary" /> Warranty Proof
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Commercial Invoices & Attached Documents Section */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Attached Invoices &amp; Certificates ({(asset.invoices?.length || 0) + (asset.attachments?.length || 0)})
                      </h4>
                    </div>

                    {asset.invoices.length === 0 && asset.attachments.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border p-8 text-center text-xs text-muted-foreground bg-secondary/10 space-y-2">
                        <FileText className="size-8 mx-auto text-muted-foreground/50 mb-1" />
                        <p className="font-semibold text-foreground">No invoices or documents are attached yet.</p>
                        <p className="text-xs text-muted-foreground">
                          Upload commercial invoices, PO receipts, or OEM warranty certificates.
                        </p>
                        <div className="flex items-center justify-center gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => handleOpenAddDocModal('invoice')}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs cursor-pointer"
                          >
                            <Plus className="size-3.5" /> Upload Invoice
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenAddDocModal('warranty')}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-secondary text-foreground text-xs font-semibold transition-colors cursor-pointer"
                          >
                            <Plus className="size-3.5" /> Add Certificate
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {asset.invoices.map((inv) => (
                          <div key={inv.id} className="rounded-lg border border-border bg-card p-3.5 space-y-2.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileSpreadsheet className="size-4 shrink-0 text-success" />
                                <div className="min-w-0">
                                  <h4 className="truncate text-foreground text-sm font-semibold">{inv.fileName}</h4>
                                  <span className="text-xs text-muted-foreground">
                                    Invoice {inv.invoiceNumber || NONE} · {inv.amount ? money(inv.amount) : NONE}{inv.vendorName ? ` · ${inv.vendorName}` : ''}{inv.fileSize ? ` · ${inv.fileSize}` : ''}
                                  </span>
                                </div>
                              </div>
                              <button type="button" onClick={() => handleDeleteInvoice(inv.id)} className="text-muted-foreground hover:text-destructive cursor-pointer" title="Remove">
                                <Trash2 className="size-3.5" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => handleOpenInvoiceDoc(inv)} className="flex-1 py-1 rounded border border-border bg-card hover:bg-secondary text-xs font-semibold text-foreground cursor-pointer">View</button>
                              <button type="button" onClick={() => handleDownloadDocument(inv.fileName, inv.fileUrl)} className="size-7 rounded border border-border bg-card hover:bg-secondary flex items-center justify-center text-foreground cursor-pointer" title="Download">
                                <Download className="size-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {asset.attachments.map((att) => (
                          <div key={att.id} className="rounded-lg border border-border bg-card p-3.5 space-y-2.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                {att.type === 'warranty' ? <ShieldCheck className="size-4 shrink-0 text-primary" /> : <FileIcon className="size-4 shrink-0 text-muted-foreground" />}
                                <div className="min-w-0">
                                  <h4 className="truncate text-foreground text-sm font-semibold">{att.name}</h4>
                                  <span className="text-xs text-muted-foreground">
                                    {att.type === 'warranty' ? 'Warranty' : 'Document'} · {att.fileName}{att.fileSize ? ` (${att.fileSize})` : ''}
                                  </span>
                                </div>
                              </div>
                              <button type="button" onClick={() => handleDeleteAttachment(att.id)} className="text-muted-foreground hover:text-destructive cursor-pointer" title="Remove">
                                <Trash2 className="size-3.5" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => handleOpenWarrantyDoc(att)} className="flex-1 py-1 rounded border border-border bg-card hover:bg-secondary text-xs font-semibold text-foreground cursor-pointer">View</button>
                              <button type="button" onClick={() => handleDownloadDocument(att.fileName, att.fileUrl)} className="size-7 rounded border border-border bg-card hover:bg-secondary flex items-center justify-center text-foreground cursor-pointer" title="Download">
                                <Download className="size-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 4: ASSOCIATED SUPPORT TICKETS (CONNECTED TO 500 SEEDED TICKETS) */}
              {activeTab === 'tickets' && (
                <div className="space-y-4">
                  {/* NEWLY CREATED TICKET BANNER */}
                  {lastCreatedTicket && (
                    <div className="flex items-center justify-between p-3.5 rounded-lg border border-primary/30 bg-primary/5 text-xs text-foreground shadow-2xs">
                      <div className="flex items-center gap-3">
                        <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-mono font-bold text-xs shrink-0">
                          <Check className="size-4" />
                        </span>
                        <div>
                          <p className="font-semibold text-foreground flex items-center gap-2">
                            <span>Ticket Logged:</span>
                            <span className="font-mono text-primary font-bold text-xs px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20">
                              {lastCreatedTicket.ticketNumber}
                            </span>
                            <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-success/15 text-success border border-success/30">
                              Logged in Support
                            </span>
                          </p>
                          <p className="text-muted-foreground text-[11px] mt-0.5">
                            {lastCreatedTicket.subject} — Linked to asset {asset.asset_code}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => navigate(`/support/tickets?ticketId=${lastCreatedTicket.ticketNumber}`)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-secondary font-semibold text-xs text-foreground transition-colors shadow-2xs"
                        >
                          View in Support <ArrowUpRight className="size-3 text-muted-foreground" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setLastCreatedTicket(null)}
                          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                          title="Dismiss notification"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setTicketFilter('all')}
                        className={cn(
                          'px-2.5 py-1 rounded text-xs font-semibold transition-colors',
                          ticketFilter === 'all'
                            ? 'bg-card text-foreground border border-border shadow-xs font-semibold'
                            : 'bg-secondary text-muted-foreground hover:text-foreground'
                        )}
                      >
                        All Tickets ({linkedTickets.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setTicketFilter('open')}
                        className={cn(
                          'px-2.5 py-1 rounded text-xs font-semibold transition-colors',
                          ticketFilter === 'open'
                            ? 'bg-card text-foreground border border-border shadow-xs font-semibold'
                            : 'bg-secondary text-muted-foreground hover:text-foreground'
                        )}
                      >
                        Active Issues ({openTicketsCount})
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleOpenTicketModal}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
                    >
                      <Plus className="size-3.5" /> Create Ticket for Asset
                    </button>
                  </div>

                  {displayedTickets.length > 0 ? (
                    <div className="rounded-lg border border-border overflow-x-auto bg-card">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="border-b border-border bg-secondary/50 text-muted-foreground">
                            <th className="py-2.5 px-3 font-semibold w-28">Ticket #</th>
                            <th className="py-2.5 px-3 font-semibold">Subject</th>
                            <th className="py-2.5 px-3 font-semibold w-24">Priority</th>
                            <th className="py-2.5 px-3 font-semibold w-28">Status</th>
                            <th className="py-2.5 px-3 font-semibold w-36">Requester</th>
                            <th className="py-2.5 px-3 font-semibold w-28">Created</th>
                            <th className="py-2.5 px-3 font-semibold w-16 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {displayedTickets.map((t) => {
                            const isOpen = t.status !== 'resolved' && t.status !== 'closed';
                            const isNewlyCreated =
                              lastCreatedTicket &&
                              (t.ticketId === lastCreatedTicket.ticketNumber ||
                               String(t.id) === String(lastCreatedTicket.id) ||
                               (t.ticketNumber && t.ticketNumber === lastCreatedTicket.ticketNumber));
                            return (
                              <tr
                                key={t.id}
                                className={cn(
                                  'hover:bg-secondary/30 transition-colors group',
                                  isNewlyCreated && 'bg-primary/5 font-medium'
                                )}
                              >
                                <td className="py-2.5 px-3 font-mono font-semibold text-primary">
                                  <div className="flex items-center gap-1.5">
                                    <span>{t.ticketId || `TB#${t.id}`}</span>
                                    {isNewlyCreated && (
                                      <span className="text-[9px] uppercase px-1 py-0.2 rounded bg-primary/10 text-primary font-bold">New</span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2.5 px-3 font-medium text-foreground">
                                  <div className="truncate max-w-xs">{t.subject}</div>
                                </td>
                                <td className="py-2.5 px-3">
                                  <span
                                    className={cn(
                                      'text-xs font-semibold px-2 py-0.5 rounded border capitalize',
                                      t.priority === 'urgent'
                                        ? 'bg-destructive/10 text-destructive border-destructive/20'
                                        : t.priority === 'high'
                                        ? 'bg-warning/10 text-warning-strong border-warning/20'
                                        : 'bg-secondary text-muted-foreground border-border'
                                    )}
                                  >
                                    {t.priority}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3">
                                  <span
                                    className={cn(
                                      'text-xs font-semibold px-2 py-0.5 rounded border capitalize',
                                      isOpen
                                        ? 'bg-primary/10 text-primary-strong border-primary/20'
                                        : 'bg-success/10 text-success-strong border-success/20'
                                    )}
                                  >
                                    {t.status}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-muted-foreground truncate">
                                  {t.requesterName || t.client || 'Employee'}
                                </td>
                                <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">
                                  {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}
                                </td>
                                <td className="py-2.5 px-3 text-right">
                                  <button
                                    onClick={() => navigate(`/support/tickets?ticketId=${t.ticketId || t.id}`)}
                                    className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                    title="View Ticket in ITSM Desk"
                                  >
                                    <ArrowUpRight className="size-3.5" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-8 text-center rounded-lg border border-dashed border-border bg-secondary/10 space-y-2">
                      <ShieldCheck className="size-8 mx-auto text-muted-foreground/60" />
                      <p className="font-semibold text-foreground text-xs">No support tickets found</p>
                      <p className="text-muted-foreground text-xs">
                        {ticketFilter === 'open' ? 'There are no active or unresolved issues on this asset.' : 'No support issues or maintenance tickets have been filed against this asset yet.'}
                      </p>
                      <button
                        type="button"
                        onClick={handleOpenTicketModal}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                      >
                        <Plus className="size-3" /> Report First Issue
                      </button>
                    </div>
                  )}
                </div>
              )}



              {/* TAB 4: PHOTO & MEDIA VAULT (WEBP / PNG / JPG / SCREENSHOTS) */}
              {!isCloudOrSoftware && activeTab === 'photos' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-3">
                    <div>
                      <h3 className="text-foreground font-display text-card-title">
                        Device Photo Vault ({asset.images?.length || 0} Images)
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Supports WebP, PNG, JPEG, SVG. Drag &amp; drop or press <kbd className="px-1 py-0.2 rounded bg-secondary border border-border font-mono text-xs">Ctrl+V</kbd> to paste directly.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStagedPhotos([]);
                        setNewImageUrl('');
                        setImageUploadMode('file');
                        setShowAddImageModal(true);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
                    >
                      <Upload className="size-3.5" /> Upload Photo
                    </button>
                  </div>

                  {asset.images && asset.images.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {asset.images.map((imgUrl, idx) => (
                        <div
                          key={idx}
                          className="relative aspect-square rounded-md border border-border bg-secondary/40 overflow-hidden group shadow-2xs"
                        >
                          <img
                            src={imgUrl}
                            alt={`Asset Photo ${idx + 1}`}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                            onClick={() => setLightboxImage({ isOpen: true, url: imgUrl, index: idx })}
                          />
                          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-card/90 border border-border text-xs font-mono text-foreground backdrop-blur-xs">
                            #{idx + 1}
                          </div>
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setLightboxImage({ isOpen: true, url: imgUrl, index: idx })}
                              className="size-7 rounded bg-card text-foreground flex items-center justify-center hover:bg-secondary"
                              title="Enlarge"
                            >
                              <ZoomIn className="size-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownloadDocument(`Asset_${asset.asset_code}_Photo_${idx + 1}.jpg`, imgUrl)}
                              className="size-7 rounded bg-card text-foreground flex items-center justify-center hover:bg-secondary"
                              title="Download"
                            >
                              <Download className="size-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeletePhoto(idx)}
                              className="size-7 rounded bg-destructive text-destructive-foreground flex items-center justify-center hover:opacity-90"
                              title="Delete"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center rounded-lg border border-dashed border-border bg-secondary/10 space-y-2">
                      <ImageIcon className="size-8 mx-auto text-muted-foreground/60" />
                      <p className="font-semibold text-foreground text-xs">No photos in media vault</p>
                      <p className="text-muted-foreground text-xs">
                        Upload hardware photos or take a screenshot and press Ctrl+V to attach device condition snaps.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setStagedPhotos([]);
                          setNewImageUrl('');
                          setImageUploadMode('file');
                          setShowAddImageModal(true);
                        }}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                      >
                        <Upload className="size-3" /> Upload Device Snap
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 5: ACTIVITY & AUDIT TIMELINE */}
              {activeTab === 'history' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <div>
                      <h3 className="text-foreground font-display text-card-title">Asset Lifecycle Audit Trail</h3>
                      <p className="text-xs text-muted-foreground">Immutable compliance and chain-of-custody logs</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setModalInput(EMPTY_MODAL);
                        setActiveModal('maintenance');
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-secondary text-foreground text-xs font-semibold transition-colors"
                    >
                      <Wrench className="size-3.5" /> Log Maintenance Note
                    </button>
                  </div>

                  {/* Timeline from assets.asset_history */}
                  {history.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                      No recorded activity for this asset yet.
                    </div>
                  ) : (
                    <div className="relative pl-6 space-y-3 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
                      {history.map((h) => (
                        <div key={h.id} className="relative">
                          <div className="absolute -left-6 top-1 size-3 rounded-full bg-primary ring-4 ring-card" />
                          <div className="rounded-md border border-border bg-secondary/20 p-3 space-y-1">
                            <div className="flex items-center justify-between gap-2 text-xs">
                              <span className="font-semibold capitalize text-foreground">{h.action.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{h.details || NONE}</p>
                            <p className="text-xs text-muted-foreground">
                              {h.fromStatus && h.toStatus && h.fromStatus !== h.toStatus ? `${h.fromStatus} → ${h.toStatus} · ` : ''}by {h.performedBy}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* === WORKFLOW MODALS === */}

      {/* 1. CHECK-OUT / ASSIGN MODAL (Right Slide-Over Panel) */}
      {activeModal === 'checkout' && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <UserCheck className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                    Check-Out Asset
                    <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-secondary text-foreground font-medium border border-border">
                      {asset.asset_code}
                    </span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Assign custody of this hardware asset to an employee
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleCheckOutSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 space-y-5">
                {/* Asset Summary Banner */}
                <div className="rounded-lg border border-border bg-secondary/30 p-3.5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="size-10 rounded-lg bg-card border border-border flex items-center justify-center text-primary shrink-0">
                      <Laptop className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[asset.manufacturer, asset.model].filter(Boolean).join(' · ') || asset.category || 'Hardware Asset'}
                        {asset.serial_number ? ` · Serial: ${asset.serial_number}` : ''}
                      </p>
                    </div>
                  </div>
                  <Badge variant={asset.status === 'In Stock' ? 'success' : 'info'} dot className="shrink-0">
                    {asset.status || 'Available'}
                  </Badge>
                </div>

                {/* Assignee / Employee Picker */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-foreground">
                    Assignee / Employee <span className="text-destructive">*</span>
                  </label>

                  {selectedEmployee ? (
                    /* Selected Employee Preview Card */
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 transition-all">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <Avatar size="md" className="border border-primary/20 shrink-0">
                            <AvatarFallback className="bg-primary/10 text-primary-strong font-semibold">
                              {initials(selectedEmployee.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-foreground">{selectedEmployee.name}</span>
                              {selectedEmployee.code && (
                                <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-card border border-border text-muted-foreground">
                                  {selectedEmployee.code}
                                </span>
                              )}
                              {selectedEmployee.status === 'terminated' || selectedEmployee.status === 'resigned' ? (
                                <Badge variant="destructive" className="text-[10px] h-5">{selectedEmployee.status}</Badge>
                              ) : null}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {[selectedEmployee.designation, selectedEmployee.department].filter(Boolean).join(' · ') || 'No department assigned'}
                            </p>

                            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                              {selectedEmployee.email && (
                                <span className="inline-flex items-center gap-1.5">
                                  <Mail className="size-3.5 text-muted-foreground/70" />
                                  <span className="font-mono text-[11px]">{selectedEmployee.email}</span>
                                </span>
                              )}
                              {selectedEmployee.location && (
                                <span className="inline-flex items-center gap-1.5">
                                  <MapPin className="size-3.5 text-muted-foreground/70" />
                                  <span>{selectedEmployee.location}</span>
                                </span>
                              )}
                              <span className="inline-flex items-center gap-1.5">
                                <Briefcase className="size-3.5 text-muted-foreground/70" />
                                <span>{selectedEmployee.assetCount || 0} active asset(s) held</span>
                              </span>
                            </div>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setModalInput((prev) => ({ ...prev, employeeId: '', department: '' }));
                            setEmployeeSearch('');
                          }}
                          className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground shrink-0"
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Search & Selection Box */
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Search employee by name, code (e.g. TB000050), or department..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          className="w-full h-10 pl-9 pr-3 rounded-lg border border-input bg-background text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                          autoFocus
                        />
                      </div>

                      <div className="rounded-lg border border-border bg-card max-h-48 overflow-y-auto divide-y divide-border/60 shadow-xs">
                        {filteredEmployees.length === 0 ? (
                          <div className="p-4 text-center text-xs text-muted-foreground">
                            No employees found matching &quot;{employeeSearch}&quot;
                          </div>
                        ) : (
                          filteredEmployees.map((emp) => (
                            <button
                              key={emp.id}
                              type="button"
                              onClick={() => {
                                setModalInput((prev) => ({
                                  ...prev,
                                  employeeId: emp.id,
                                  department: emp.department || '',
                                  site: prev.site || emp.location || '',
                                }));
                                setEmployeeSearch('');
                              }}
                              className="w-full px-3.5 py-2.5 text-left flex items-center justify-between gap-3 hover:bg-secondary/60 transition-colors group"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <Avatar size="sm" className="shrink-0">
                                  <AvatarFallback className="bg-primary/10 text-primary-strong text-xs font-semibold">
                                    {initials(emp.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                                      {emp.name}
                                    </span>
                                    {emp.code && (
                                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                                        {emp.code}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-muted-foreground truncate">
                                    {[emp.designation, emp.department].filter(Boolean).join(' · ') || 'No department'}
                                    {emp.location ? ` · ${emp.location}` : ''}
                                  </p>
                                </div>
                              </div>
                              <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                Select
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Custody & Location Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Department
                    </label>
                    <div className="h-9 px-3 rounded-lg border border-input bg-secondary/30 flex items-center text-xs font-medium text-foreground">
                      {modalInput.department || (selectedEmployee?.department) || NONE}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Site / Office Location
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="e.g. HQ Floor 3 or Chennai Office"
                        value={modalInput.site}
                        onChange={(e) => setModalInput({ ...modalInput, site: e.target.value })}
                        className="w-full h-9 pl-9 pr-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                      />
                    </div>
                  </div>
                </div>

                {/* Handover Notes & Included Accessories */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold text-foreground">
                      Handover Notes &amp; Accessories
                    </label>
                    <span className="text-[11px] text-muted-foreground">Optional</span>
                  </div>

                  {/* Quick Accessory Badges */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] text-muted-foreground">Quick add:</span>
                    {['Power Adapter', 'USB-C Cable', 'Laptop Sleeve', 'Wireless Mouse', 'Display Adapter'].map((acc) => (
                      <button
                        key={acc}
                        type="button"
                        onClick={() => {
                          const current = modalInput.notes.trim();
                          const addition = `Includes ${acc}`;
                          if (!current) {
                            setModalInput({ ...modalInput, notes: addition });
                          } else if (!current.toLowerCase().includes(acc.toLowerCase())) {
                            setModalInput({ ...modalInput, notes: `${current}, ${addition}` });
                          }
                        }}
                        className="text-[11px] px-2 py-0.5 rounded-md border border-border bg-card hover:bg-secondary text-foreground hover:border-primary/40 transition-colors"
                      >
                        + {acc}
                      </button>
                    ))}
                  </div>

                  <textarea
                    rows={2}
                    placeholder="e.g. Handed over with 96W power adapter and USB-C cable in good working condition..."
                    value={modalInput.notes}
                    onChange={(e) => setModalInput({ ...modalInput, notes: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5 hidden sm:inline-flex">
                  <ShieldCheck className="size-4 text-primary" /> Handover recorded in audit history
                </span>
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!modalInput.employeeId}
                  >
                    <UserCheck className="size-4" /> Confirm Check-Out
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. CHECK-IN / RETURN MODAL (Right Slide-Over Panel) */}
      {activeModal === 'checkin' && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <RotateCcw className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                    Check-In Asset
                    <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-secondary text-foreground font-medium border border-border">
                      {asset.asset_code}
                    </span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Return this asset to inventory or route for maintenance
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleCheckInSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 space-y-5">
                {/* Asset Summary Banner */}
                <div className="rounded-lg border border-border bg-secondary/30 p-3.5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="size-10 rounded-lg bg-card border border-border flex items-center justify-center text-primary shrink-0">
                      <Laptop className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[asset.manufacturer, asset.model].filter(Boolean).join(' · ') || asset.category || 'Hardware Asset'}
                        {asset.assignedToName ? ` · Held by: ${asset.assignedToName}` : ''}
                      </p>
                    </div>
                  </div>
                  <Badge variant="warning" dot className="shrink-0">
                    Currently Assigned
                  </Badge>
                </div>

                {/* Return Condition Interactive Cards */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-foreground">
                    Device Return Condition <span className="text-destructive">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {[
                      {
                        value: 'Good',
                        label: 'Good / Ready',
                        desc: 'Ready for re-deployment to In Stock',
                        badgeVar: 'success' as const,
                      },
                      {
                        value: 'Needs Repair',
                        label: 'Needs Repair',
                        desc: 'Create maintenance repair ticket',
                        badgeVar: 'warning' as const,
                      },
                      {
                        value: 'Damaged',
                        label: 'Damaged',
                        desc: 'Defective / write-off assessment',
                        badgeVar: 'destructive' as const,
                      },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setModalInput({ ...modalInput, condition: opt.value })}
                        className={cn(
                          'p-3 rounded-lg border text-left flex flex-col justify-between transition-all',
                          modalInput.condition === opt.value
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-border bg-card hover:bg-secondary/40'
                        )}
                      >
                        <div className="flex items-center justify-between w-full mb-1">
                          <span className="text-xs font-semibold text-foreground">{opt.label}</span>
                          <span
                            className={cn(
                              'size-3 rounded-full border flex items-center justify-center',
                              modalInput.condition === opt.value
                                ? 'border-primary bg-primary'
                                : 'border-muted-foreground/40'
                            )}
                          >
                            {modalInput.condition === opt.value && <span className="size-1 bg-white rounded-full" />}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-tight">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Inspection & Return Notes */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold text-foreground">
                      Inspection &amp; Return Notes
                    </label>
                    <span className="text-[11px] text-muted-foreground">Optional</span>
                  </div>

                  {/* Quick Return Chips */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] text-muted-foreground">Quick add:</span>
                    {['OS wiped', 'All accessories returned', 'Charger returned', 'Minor cosmetic wear', 'Screen inspected'].map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => {
                          const current = modalInput.notes.trim();
                          if (!current) {
                            setModalInput({ ...modalInput, notes: chip });
                          } else if (!current.toLowerCase().includes(chip.toLowerCase())) {
                            setModalInput({ ...modalInput, notes: `${current}, ${chip}` });
                          }
                        }}
                        className="text-[11px] px-2 py-0.5 rounded-md border border-border bg-card hover:bg-secondary text-foreground hover:border-primary/40 transition-colors"
                      >
                        + {chip}
                      </button>
                    ))}
                  </div>

                  <textarea
                    rows={3}
                    placeholder="e.g. Device wiped, cleaned, charger returned. Asset verified and ready for inventory..."
                    value={modalInput.notes}
                    onChange={(e) => setModalInput({ ...modalInput, notes: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                >
                  <RotateCcw className="size-4" /> Confirm Check-In
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. LOG MAINTENANCE MODAL (Right Slide-Over Panel) */}
      {activeModal === 'maintenance' && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <Wrench className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                    Log Maintenance Event
                    <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-secondary text-foreground font-medium border border-border">
                      {asset.asset_code}
                    </span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Record servicing, diagnostics, or hardware repairs
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleLogMaintenanceSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Service / Technician Provider
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Apple Authorized Service / In-House IT Specialist"
                    value={modalInput.serviceProvider}
                    onChange={(e) => setModalInput({ ...modalInput, serviceProvider: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Diagnosis &amp; Service Performed <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    rows={4}
                    required
                    placeholder="Describe maintenance actions, diagnostic results, replaced parts, or firmware updates..."
                    value={modalInput.notes}
                    onChange={(e) => setModalInput({ ...modalInput, notes: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                >
                  <Wrench className="size-4" /> Record Maintenance
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. PRINT BARCODE & INDUSTRIAL ITAM LABEL MODAL (Right Slide-Over Panel) */}
      {SHOW_BARCODE && activeModal === 'barcode' && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <Barcode className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                    Industrial ITAM Barcode Tag
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Thermal adhesive tag formatted for industrial barcode readers
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Print Preview Canvas (Styled for thermal / adhesive label printers) */}
              <div className="printable-barcode-tag border-2 border-dashed border-border p-4 rounded-lg bg-white text-slate-900 space-y-3 text-center shadow-xs">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1.5 text-xs">
                  <span className="font-bold text-blue-600 tracking-wide uppercase text-[11px]">{(asset as any)?.company || 'TEAMONE'}</span>
                  <span className="text-slate-500 font-semibold text-[10px] uppercase tracking-wider">Property Of {(asset as any)?.company || 'TeamOne'}</span>
                </div>

                <div className="space-y-0.5 text-left">
                  <h4 className="text-slate-900 text-sm font-bold tracking-tight">{asset.name}</h4>
                  <p className="font-mono text-xs text-slate-600 font-medium">
                    {asset.manufacturer ? `${asset.manufacturer} · ` : ''}{asset.model || asset.category || 'IT Asset'}
                  </p>
                </div>

                {/* 1D Code 128 Barcode Canvas */}
                <div className="py-1 flex flex-col items-center justify-center bg-white rounded border border-slate-200 p-2">
                  <svg ref={modalBarcodeSvgRef} className="max-w-full h-auto" />
                  <div className="flex items-center justify-between w-full font-mono text-[11px] text-slate-500 mt-1 px-1">
                    <span>TAG: <strong className="text-slate-900">{asset.asset_code}</strong></span>
                    <span>SN: <strong className="text-slate-900">{asset.serial_number || NONE}</strong></span>
                  </div>
                </div>

                {/* Dynamic Assigned Person Status based on Live Application State */}
                <div className="text-xs border-t border-slate-200 pt-2 text-left bg-slate-50 p-2.5 rounded space-y-1.5">
                  <div className="flex justify-between items-center text-[10px] text-slate-500">
                    <span className="font-semibold uppercase tracking-wider">Custody & Allocation Details</span>
                    <span className={cn(
                      'px-1.5 py-0.5 rounded font-semibold text-[10px]',
                      asset.status === 'Assigned' ? 'bg-blue-100 text-blue-700' :
                      asset.status === 'In Stock' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-amber-100 text-amber-700'
                    )}>
                      {asset.status || 'Active'}
                    </span>
                  </div>
                  <div className="font-bold text-slate-900 truncate text-xs flex items-center justify-between">
                    <span>{custodyDetails.name}</span>
                    {custodyDetails.code && <span className="font-mono text-[10px] text-slate-500">{custodyDetails.code}</span>}
                  </div>
                  {(custodyDetails.dept || custodyDetails.loc) && (
                    <div className="text-[11px] text-slate-600 flex flex-wrap gap-x-2">
                      {custodyDetails.dept && <span>Dept: {custodyDetails.dept}</span>}
                      {custodyDetails.loc && <span>Loc: {custodyDetails.loc}</span>}
                    </div>
                  )}
                  {/* Hardware Spec Summary */}
                  <div className="text-[10px] text-slate-500 border-t border-slate-200 pt-1 flex justify-between font-mono">
                    <span>Classification: {asset.category || asset.type || 'Hardware'}</span>
                    <span>Warranty: {warrantyDays !== null ? (warrantyDays > 0 ? `${warrantyDays}d left` : 'Expired') : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
              {barcodeDataUrl ? (
                <a
                  href={barcodeDataUrl}
                  download={`${asset.asset_code}_Barcode.png`}
                  className="px-3 py-1.5 rounded-lg border border-border text-foreground hover:bg-secondary font-semibold text-xs inline-flex items-center gap-1.5 transition-colors"
                >
                  <Download className="size-3.5" /> Download Barcode
                </a>
              ) : <div />}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeModal}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => window.print()}
                >
                  <Printer className="size-4" /> Print Tag
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. RETIRE ASSET MODAL (Right Slide-Over Panel) */}
      {activeModal === 'retire' && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-destructive/5 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center shrink-0 border border-destructive/20">
                  <Trash2 className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-destructive flex items-center gap-2">
                    Decommission Asset
                    <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-secondary text-foreground font-medium border border-border">
                      {asset.asset_code}
                    </span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Remove asset from production availability and record as retired
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleRetireSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                <div className="p-3.5 rounded-lg border border-warning/30 bg-warning/5 text-warning-strong text-xs flex items-start gap-2.5">
                  <AlertTriangle className="size-4 shrink-0 mt-0.5 text-warning" />
                  <p>
                    Retiring this asset removes it from active inventory. Any active licenses or assignments should be revoked before retiring.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Decommission Reason <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    rows={3}
                    required
                    placeholder="e.g. End-of-life cycle reached, hardware damaged beyond repair, e-waste recycler pickup..."
                    value={modalInput.reason}
                    onChange={(e) => setModalInput({ ...modalInput, reason: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-destructive hover:underline text-xs font-semibold"
                >
                  Delete Record Permanently
                </button>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                  >
                    Retire Asset
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5B. RAISE SUPPORT TICKET MODAL (In-App Slide-Over Panel) */}
      {activeModal === 'ticket' && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <Ticket className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                    Raise Support Ticket
                    <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-secondary text-foreground font-medium border border-border">
                      {asset.asset_code}
                    </span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    File a new IT support ticket directly without leaving the asset workspace
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleCreateTicketSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                {/* Linked Asset Summary Pill */}
                <div className="p-3 rounded-lg border border-border bg-secondary/30 flex items-center justify-between gap-3 text-xs">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{asset.name}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">({asset.asset_code})</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span>{asset.category || asset.type || 'Hardware'}</span>
                      {asset.serial_number && <span>• SN: {asset.serial_number}</span>}
                      <span>• Status: {asset.status}</span>
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-primary/10 text-primary font-semibold border border-primary/20">
                    Linked Asset
                  </span>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Subject / Summary <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                    placeholder="e.g. Laptop screen flickering or battery not charging"
                  />
                </div>

                {/* Category & Type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Ticket Type
                    </label>
                    <select
                      value={ticketForm.type}
                      onChange={(e) => setTicketForm({ ...ticketForm, type: e.target.value })}
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="Fault">Fault / Breakdown</option>
                      <option value="Incident">Incident</option>
                      <option value="Service Request">Service Request</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Problem">Hardware Defect</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Category
                    </label>
                    <input
                      type="text"
                      value={ticketForm.category}
                      onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                      placeholder="e.g. Laptops, Hardware, Peripherals"
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>

                {/* Priority, Urgency & Impact */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Priority
                    </label>
                    <select
                      value={ticketForm.priority}
                      onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value as any })}
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary capitalize"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Urgency
                    </label>
                    <select
                      value={ticketForm.urgency}
                      onChange={(e) => setTicketForm({ ...ticketForm, urgency: e.target.value as any })}
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary capitalize"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Impact
                    </label>
                    <select
                      value={ticketForm.impact}
                      onChange={(e) => setTicketForm({ ...ticketForm, impact: e.target.value as any })}
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary capitalize"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Requester Details */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Requester Name
                    </label>
                    <input
                      type="text"
                      value={ticketForm.requesterName}
                      onChange={(e) => setTicketForm({ ...ticketForm, requesterName: e.target.value })}
                      placeholder="Employee or User name"
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Requester Email
                    </label>
                    <input
                      type="email"
                      value={ticketForm.requesterEmail}
                      onChange={(e) => setTicketForm({ ...ticketForm, requesterEmail: e.target.value })}
                      placeholder="user@example.com"
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Description & Symptoms
                  </label>
                  <textarea
                    rows={4}
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                    placeholder="Provide details about the issue, diagnostic steps tried, or error messages encountered..."
                    className="w-full p-2.5 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-y placeholder:text-muted-foreground"
                  />
                </div>

                <div className="rounded-md bg-secondary/50 p-2.5 text-[11px] text-muted-foreground flex items-center gap-2 border border-border">
                  <ShieldCheck className="size-4 text-primary shrink-0" />
                  <span>
                    Submitting this form records the ticket into the IT Support service with asset tag{' '}
                    <strong className="text-foreground">{asset.asset_code}</strong> linked automatically.
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeModal}
                  disabled={isSubmittingTicket}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmittingTicket}
                >
                  {isSubmittingTicket ? (
                    <>
                      <RefreshCw className="size-3.5 animate-spin mr-1.5" /> Creating Ticket...
                    </>
                  ) : (
                    <>
                      <Plus className="size-3.5 mr-1.5" /> Create Ticket
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. ADD PHOTO MODAL (Right Slide-Over Panel) */}
      {showAddImageModal && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={() => setShowAddImageModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <UploadCloud className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-1.5">
                    Upload Asset Photo
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Attach images to this asset record
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAddImageModal(false)}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleAddPhotoSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 space-y-4 text-xs">
                <div className="flex rounded-lg border border-border p-1 bg-secondary/50">
                  <button
                    type="button"
                    onClick={() => setImageUploadMode('file')}
                    className={cn(
                      'flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors',
                      imageUploadMode === 'file' ? 'bg-card text-foreground shadow-2xs' : 'text-muted-foreground'
                    )}
                  >
                    Upload Local File(s)
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageUploadMode('url')}
                    className={cn(
                      'flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors',
                      imageUploadMode === 'url' ? 'bg-card text-foreground shadow-2xs' : 'text-muted-foreground'
                    )}
                  >
                    Image URL
                  </button>
                </div>

                {imageUploadMode === 'file' ? (
                  <div className="space-y-3">
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDraggingPhotos(true); }}
                      onDragLeave={() => setIsDraggingPhotos(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDraggingPhotos(false);
                        handlePhotoFilesSelected(e.dataTransfer.files);
                      }}
                      onClick={() => photoFileInputRef.current?.click()}
                      className={cn(
                        'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                        isDraggingPhotos ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/40 bg-secondary/20'
                      )}
                    >
                      <input
                        ref={photoFileInputRef}
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                        className="hidden"
                        onChange={(e) => handlePhotoFilesSelected(e.target.files)}
                      />
                      <UploadCloud className="size-8 mx-auto text-muted-foreground/60 mb-2" />
                      <p className="font-semibold text-foreground text-xs">Click or drag &amp; drop photos here</p>
                      <p className="text-muted-foreground text-xs mt-1">
                        WebP, PNG, JPG supported. (You can also press Ctrl+V anywhere!)
                      </p>
                    </div>

                    {stagedPhotos.length > 0 && (
                      <div className="space-y-1.5">
                        <span className="text-xs font-semibold text-foreground">
                          Selected Files ({stagedPhotos.length}):
                        </span>
                        <div className="max-h-32 overflow-y-auto divide-y divide-border border border-border rounded-md bg-background">
                          {stagedPhotos.map((p) => (
                            <div key={p.id} className="flex items-center justify-between p-2 text-xs">
                              <span className="truncate max-w-[280px] font-mono text-xs">{p.name}</span>
                              <span className="text-muted-foreground text-xs">{p.size}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">Public Image URL</label>
                    <input
                      type="url"
                      placeholder="https://images.unsplash.com/photo-..."
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAddImageModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                >
                  Save to Vault
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. ADD DOCUMENT / INVOICE MODAL (Right Slide-Over Panel) */}
      {showAddDocModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={() => setShowAddDocModal({ isOpen: false, type: 'invoice' })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <FileUp className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-1.5">
                    {showAddDocModal.type === 'invoice' ? 'Upload Commercial Tax Invoice' : 'Upload ITAM Certificate'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Attach document to this asset record
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAddDocModal({ isOpen: false, type: 'invoice' })}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveDocumentSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 space-y-4 text-xs">
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDraggingDoc(true); }}
                  onDragLeave={() => setIsDraggingDoc(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingDoc(false);
                    handleDocFileSelected(e.dataTransfer.files);
                  }}
                  onClick={() => docFileInputRef.current?.click()}
                  className={cn(
                    'border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors',
                    isDraggingDoc ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/40 bg-secondary/20'
                  )}
                >
                  <input
                    ref={docFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={(e) => handleDocFileSelected(e.target.files)}
                  />
                  <FileUp className="size-7 mx-auto text-muted-foreground/60 mb-1.5" />
                  <p className="font-semibold text-foreground text-xs">
                    {stagedDoc ? `Selected: ${stagedDoc.name}` : 'Click or drop PDF / image receipt here'}
                  </p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    {stagedDoc ? `File Size: ${stagedDoc.size}` : 'PDF, DOCX, PNG, JPG, WEBP'}
                  </p>
                </div>

                {showAddDocModal.type === 'invoice' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Invoice Number *</label>
                      <input
                        type="text"
                        required
                        value={newDocMeta.invoiceNumber}
                        onChange={(e) => setNewDocMeta({ ...newDocMeta, invoiceNumber: e.target.value })}
                        className="w-full h-9 px-3 rounded-lg border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Vendor Name</label>
                      <input
                        type="text"
                        value={newDocMeta.vendorName}
                        onChange={(e) => setNewDocMeta({ ...newDocMeta, vendorName: e.target.value })}
                        className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Total Amount ($)</label>
                      <input
                        type="number"
                        value={newDocMeta.amount}
                        onChange={(e) => setNewDocMeta({ ...newDocMeta, amount: Number(e.target.value) })}
                        className="w-full h-9 px-3 rounded-lg border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Invoice Date</label>
                      <input
                        type="date"
                        value={newDocMeta.docDate}
                        onChange={(e) => setNewDocMeta({ ...newDocMeta, docDate: e.target.value })}
                        className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Certificate Title *</label>
                      <input
                        type="text"
                        required
                        value={newDocMeta.title}
                        onChange={(e) => setNewDocMeta({ ...newDocMeta, title: e.target.value })}
                        className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Coverage Valid Until</label>
                      <input
                        type="date"
                        value={newDocMeta.validUntil}
                        onChange={(e) => setNewDocMeta({ ...newDocMeta, validUntil: e.target.value })}
                        className="w-full h-9 px-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-muted-foreground"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-muted/20 shrink-0">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAddDocModal({ isOpen: false, type: 'invoice' })}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!stagedDoc}
                >
                  Upload &amp; Link to S3
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 8. DOCUMENT VIEWER MODAL (Right Slide-Over Panel) */}
      {documentModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-2xs animate-in fade-in duration-150"
          onClick={() => setDocumentModal({ ...documentModal, isOpen: false })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden"
          >
            <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                  <FileText className="size-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                    {documentModal.title}
                  </h3>
                  <span className="text-xs font-mono text-muted-foreground">{documentModal.s3Key}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDocumentModal({ ...documentModal, isOpen: false })}
                className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Document Details Sheet */}
            <div className="flex-1 overflow-y-auto space-y-4 p-6 bg-background">
              <div className="flex justify-between items-center border-b border-border pb-3">
                <div>
                  <h4 className="text-foreground text-sm font-semibold">
                    {documentModal.docType === 'invoice' ? 'COMMERCIAL TAX INVOICE' : 'OFFICIAL WARRANTY SLA CERTIFICATE'}
                  </h4>
                  <span className="font-mono text-xs text-muted-foreground">REF: {documentModal.docNumber}</span>
                </div>
                <Badge variant="success">
                  Archived on S3
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-muted-foreground block text-xs">Vendor / Issuer</span>
                  <strong className="text-foreground">{documentModal.vendor || NONE}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs">Issue Date</span>
                  <strong className="text-foreground">{documentModal.docDate || NONE}</strong>
                </div>
                {documentModal.docAmount && (
                  <div>
                    <span className="text-muted-foreground block text-xs">Total Amount</span>
                    <strong className="text-foreground font-mono">{money(documentModal.docAmount)}</strong>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground block text-xs">Associated Asset</span>
                  <strong className="text-foreground font-mono">[{asset.asset_code}] {asset.name}</strong>
                </div>
              </div>

              {documentModal.fileUrl && documentModal.isImage && (
                <div className="mt-4 border border-border rounded-lg overflow-hidden">
                  <img src={documentModal.fileUrl} alt="Document preview" className="w-full h-auto max-h-80 object-contain" />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/20 shrink-0">
              <span className="text-xs text-muted-foreground font-mono">Size: {documentModal.fileSize || 'N/A'}</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDocumentModal({ ...documentModal, isOpen: false })}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => handleDownloadDocument(documentModal.fileName || 'Document.pdf', documentModal.fileUrl)}
                >
                  <Download className="size-4" /> Download Document
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 9. LIGHTBOX IMAGE PREVIEW MODAL */}
      {lightboxImage.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in">
          <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center">
            <button
              onClick={() => setLightboxImage({ isOpen: false, url: '', index: 0 })}
              className="absolute -top-10 right-0 text-white hover:text-muted-foreground p-1"
            >
              <X className="size-6" />
            </button>

            <img
              src={lightboxImage.url}
              alt="Asset full resolution"
              className="max-h-[75vh] w-auto rounded-lg border border-white/20 object-contain shadow-2xl"
            />

            <div className="flex items-center justify-between w-full mt-4 px-2 text-white text-xs">
              <span className="font-mono">
                Asset Photo #{lightboxImage.index + 1} of {asset.images?.length || 1} — [{asset.asset_code}]
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadDocument(`Asset_${asset.asset_code}_Photo_${lightboxImage.index + 1}.jpg`, lightboxImage.url)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-card text-foreground font-semibold hover:bg-secondary transition-colors"
                >
                  <Download className="size-3.5" /> Download
                </button>
                <button
                  onClick={() => handleDeletePhoto(lightboxImage.index)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-destructive text-destructive-foreground font-semibold hover:opacity-90"
                >
                  <Trash2 className="size-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
